/**
 * API Service Layer
 * Handles all backend communication for the Agentic SEO platform
 */

const USER_SERVICE_URL = import.meta.env.VITE_USER_SERVICE_URL || 'http://localhost:5011';
const SEO_SERVICE_URL = import.meta.env.VITE_SEO_SERVICE_URL || 'http://localhost:5010';
const CAMPAIGN_SERVICE_URL = import.meta.env.VITE_CAMPAIGN_SERVICE_URL || 'http://localhost:5012';
const AUTOMATION_SERVICE_URL = import.meta.env.VITE_AUTOMATION_SERVICE_URL || 'http://localhost:5014';
const LOGGER_SERVICE_URL = import.meta.env.VITE_LOGGER_SERVICE_URL || 'http://localhost:5013';
const SOCIAL_MEDIA_SERVICE_URL = import.meta.env.VITE_SOCIAL_MEDIA_SERVICE_URL || 'http://localhost:5015';

// API Error class
export class APIError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'APIError';
    this.status = status;
  }
}

// Fired when a 401 cannot be healed by session refresh — AuthContext listens
// and logs the user out so the app never silently shows blank pages.
export const SESSION_EXPIRED_EVENT = 'agentic-seo:session-expired';
export const sessionExpired = () => window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));

// A single in-flight refresh guard — prevents a 401 storm from triggering
// multiple concurrent refresh calls.
let _refreshing: Promise<boolean> | null = null;

async function tryRefreshSession(): Promise<boolean> {
  if (!_refreshing) {
    _refreshing = fetch(`${USER_SERVICE_URL}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    })
      .then((r) => r.ok)
      .catch(() => false)
      .finally(() => { _refreshing = null; });
  }
  return _refreshing;
}

// Default request timeout (ms). Prevents hung connections (sleep/resume,
// network drop while tab is stale) from leaving pages stuck in loading.
const DEFAULT_TIMEOUT_MS = 30_000;

// Helper function for API calls
async function apiCall<T>(url: string, options: RequestInit & { timeoutMs?: number } = {}): Promise<T> {
  const headers: Record<string, string> = { ...(options.headers as Record<string, string> || {}) };
  // Drop null/empty header values so a missing API key is NOT sent as
  // `X-API-Key: null`/`X-API-Key: ""` — the request then authenticates via
  // the HTTP-only session cookie (JWT).
  for (const key of Object.keys(headers)) {
    if (!headers[key]) delete headers[key];
  }
  // Don't set Content-Type for FormData — browser must auto-set the multipart boundary
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const requestWithTimeout = (): Promise<Response> => {
    // If the caller supplied their own signal (e.g. long-running export),
    // respect it and skip the default timeout.
    if (options.signal) {
      return fetch(url, {
        ...options,
        headers,
        credentials: options.credentials || 'include',
      });
    }
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), options.timeoutMs ?? DEFAULT_TIMEOUT_MS);
    return fetch(url, {
      ...options,
      headers,
      // Send HTTP-only session cookie with every request (primary auth for SPA)
      credentials: options.credentials || 'include',
      signal: controller.signal,
    }).finally(() => clearTimeout(timer));
  };

  try {
    let response = await requestWithTimeout();

    // 401 auto-heal: attempt one session refresh, then retry once. If the
    // refresh fails the session is genuinely gone → broadcast logout so the
    // UI redirects to login instead of showing blank content.
    if (response.status === 401 && !url.includes('/auth/')) {
      const refreshed = await tryRefreshSession();
      if (refreshed) {
        response = await requestWithTimeout();
      } else {
        sessionExpired();
        throw new APIError(401, 'Session expired — please log in again');
      }
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'An error occurred' }));
      throw new APIError(response.status, error.detail || error.message || 'Request failed');
    }

    return response.json();
  } catch (err) {
    if (err instanceof APIError) throw err;
    // AbortError / network failure — rethrow as a clear message
    const isAbort = err instanceof DOMException && err.name === 'AbortError';
    throw new APIError(0, isAbort ? 'Request timed out' : 'Network error');
  }
}

// ============================================================================
// Authentication API
// ============================================================================

export interface LoginResponse {
  access_token: string;
  token_type: string;
  api_key: string;
  user: {
    id: number;
    username: string;
    email: string;
    role: 'admin' | 'user' | 'viewer';
    created_at: string;
  };
}

export interface UserProfile {
  id: number;
  username: string;
  email: string;
  role: 'admin' | 'user' | 'viewer';
  created_at: string;
}

export const authAPI = {
  login: async (username: string, password: string): Promise<LoginResponse> => {
    return apiCall<LoginResponse>(`${USER_SERVICE_URL}/api/auth/login`, {
      method: 'POST',
      body: JSON.stringify({ username, password }),
      credentials: 'include',
    });
  },

  /**
   * Login with an API key. The server validates the key and sets the
   * HTTP-only session cookie (JWT) — the key itself is never persisted
   * in the browser (XSS-safe).
   */
  loginWithApiKey: async (key: string): Promise<LoginResponse> => {
    return apiCall<LoginResponse>(`${USER_SERVICE_URL}/api/auth/login-api-key`, {
      method: 'POST',
      body: JSON.stringify({ api_key: key }),
      credentials: 'include',
    });
  },

  /** Sliding session renewal — re-issues the session cookie (JWT). */
  refreshSession: async (): Promise<UserProfile> => {
    return apiCall<UserProfile>(`${USER_SERVICE_URL}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
  },

  /** Check for existing HTTP-only session cookie and return user profile */
  getSession: async (): Promise<UserProfile> => {
    return apiCall<UserProfile>(`${USER_SERVICE_URL}/api/auth/me`, {
      credentials: 'include',
    });
  },

  /** Logout — clears the HTTP-only session cookie */
  logout: async (): Promise<void> => {
    await apiCall<{ message: string }>(`${USER_SERVICE_URL}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    });
  },

  getProfile: async (apiKey: string | null): Promise<UserProfile> => {
    return apiCall<UserProfile>(`${USER_SERVICE_URL}/api/users/me`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  updateProfile: async (apiKey: string | null, data: { username?: string; email?: string }): Promise<UserProfile> => {
    return apiCall<UserProfile>(`${USER_SERVICE_URL}/api/users/me`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  changePassword: async (apiKey: string | null, current_password: string, new_password: string): Promise<{ message: string }> => {
    return apiCall<{ message: string }>(`${USER_SERVICE_URL}/api/auth/change-password`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify({ current_password, new_password }),
    });
  },

  getUsers: async (apiKey: string | null): Promise<unknown> => {
    return apiCall<unknown>(`${USER_SERVICE_URL}/api/admin/users`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  createUser: async (apiKey: string | null, data: Record<string, unknown>): Promise<unknown> => {
    return apiCall<unknown>(`${USER_SERVICE_URL}/api/admin/users`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  changeUserRole: async (apiKey: string | null, userId: number, role: string): Promise<UserProfile> => {
    return apiCall<UserProfile>(`${USER_SERVICE_URL}/api/admin/users/${userId}/role`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify({ role }),
    });
  },

  resetUserPassword: async (apiKey: string | null, userId: number, newPassword: string): Promise<{ message: string }> => {
    return apiCall<{ message: string }>(`${USER_SERVICE_URL}/api/admin/users/${userId}/password`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify({ new_password: newPassword }),
    });
  },

  deleteUser: async (apiKey: string | null, userId: number): Promise<{ message: string }> => {
    return apiCall<{ message: string }>(`${USER_SERVICE_URL}/api/admin/users/${userId}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  generatePassword: async (apiKey: string | null, length = 16): Promise<{ password: string }> => {
    return apiCall<{ password: string }>(`${USER_SERVICE_URL}/api/admin/generate-password?length=${length}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },
};

// ============================================================================
// SEO API
// ============================================================================

export interface SeoProject {
  id: string;
  user_id: string;
  name: string;
  domain: string;
  description: string | null;
  primary_language: string;
  location_code: number;
  url_structure: 'single_language' | 'subdirectory' | 'subdomain';
  language_paths?: Record<string, string> | null;
  crawl_scope_pattern?: string | null;
  dataforseo_consented?: boolean;
  created_at: string;
  updated_at: string;
}

export interface AuditCrawlSettings {
  max_pages?: number;
  max_depth?: number;
  js_rendering_enabled?: boolean;
  concurrent_requests?: number;
  politeness_delay?: number;
  include_subdomains?: boolean;
  include_gsc_data?: boolean;
  include_ga4_data?: boolean;
  include_ai_overview?: boolean;
  include_cwv?: boolean;
  include_schema_validation?: boolean;
  include_mobile_check?: boolean;
  include_ai_analysis?: boolean;
  backlink_limit?: number;
  backlink_min_domain_authority?: number;
}

export interface Audit {
  id: string;
  project_id: string;
  audit_type: string;
  status: 'pending' | 'running' | 'crawling' | 'analyzing' | 'scoring' | 'completed' | 'failed';
  total_pages: number;
  pages_crawled: number;
  overall_score: number | null;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  celery_task_id: string | null;
  target_domain: string | null;
  crawl_settings?: AuditCrawlSettings | null;
  created_at: string;
  updated_at: string;
}

export interface AuditSchedule {
  id: string;
  project_id: string;
  user_id: string;
  audit_type: string;
  target_domain?: string | null;
  competitor_id?: string | null;
  frequency: 'daily' | 'weekly' | 'monthly';
  day_of_week?: number | null;
  time_of_day: string;
  crawl_settings?: AuditCrawlSettings | null;
  is_active: boolean;
  next_run_at?: string | null;
  last_run_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface SchedulesOverviewAuditEntry {
  id: string;
  audit_type: string;
  frequency: string;
  day_of_week: number | null;
  time_of_day: string;
  is_active: boolean;
  next_run_at: string | null;
  last_run_at: string | null;
  competitor_id: string | null;
  target_domain: string | null;
  crawl_settings?: AuditCrawlSettings | null;
}

export interface SchedulesOverviewSerpEntry {
  id: string;
  name: string;
  schedule_frequency: string | null;
  schedule_day_of_week: number | null;
  schedule_time_of_day: string;
  is_active: boolean;
  next_run_at: string | null;
  last_run_at: string | null;
  checks_count: number;
}

export interface SchedulesOverview {
  audit_schedules: SchedulesOverviewAuditEntry[];
  serp_configs: SchedulesOverviewSerpEntry[];
}

export interface Keyword {
  id: string;
  project_id: string;
  keyword: string;
  search_volume: number | null;
  difficulty: number | null;
  current_position: number | null;
  previous_position: number | null;
  url: string | null;
  tags: string[] | null;
  priority: 'high' | 'medium' | 'low' | null;
  intent: 'informational' | 'transactional' | 'navigational' | 'commercial' | null;
  is_branded: boolean;
  target_url: string | null;
  notes: string | null;
  source: string | null;
  // Lineage fields (Phase 1)
  source_domain: string | null;
  source_competitor_id: string | null;
  source_audit_id: string | null;
  source_group: string | null;
  // Lifecycle fields (Phase 1)
  discovery_status: 'discovered' | 'rediscovered' | 'active' | 'lost' | null;
  first_seen_at: string | null;
  last_seen_at: string | null;
  last_seen_position: number | null;
  last_seen_url: string | null;
  // Review queue (Phase 1)
  review_status: 'pending' | 'accepted' | 'ignored' | null;
  reviewed_at: string | null;
  last_checked_at: string | null;
  // Enrichment + cannibalization (Phase 3)
  impressions?: number | null;
  position_source?: 'gsc' | null;
  cannibalized?: boolean;
  cannibalized_pages?: string[] | null;
  // AI / GEO visibility (LLM mentions)
  ai_search_volume?: number | null;
  mentions_count?: number | null;
  ai_platforms?: string[] | null;
  created_at: string;
  updated_at: string;
}

export interface KeywordQueryResponse {
  items: Keyword[];
  total: number;
  page: number;
  per_page: number;
}

export interface UrlKeywordEntry {
  keyword: string;
  position: number | null;
  clicks: number;
  impressions: number;
  ctr: number | null;
  source: 'gsc' | 'crawl';
  wdf_idf?: number | null;
  in_title?: boolean | null;
  in_h1?: boolean | null;
  // Enrichment + tracking extras (Phase 3)
  tracked?: boolean;
  current_position?: number | null;
  previous_position?: number | null;
  difficulty?: number | null;
  intent?: string | null;
  tags?: string[] | null;
}

export interface KeywordUrlView {
  url: string;
  keywords: UrlKeywordEntry[];
  clicks: number;
  impressions: number;
  source: 'gsc' | 'crawl';
  cannibalized_keywords: string[];
  cannibalized: boolean;
}

export interface KeywordByUrlResponse {
  items: KeywordUrlView[];
  total: number;
  audit_id: string | null;
}

export interface SerpSnapshot {
  id: string;
  keyword_id: string;
  position: number | null;
  url: string | null;
  featured_snippet: boolean;
  serp_features: Record<string, unknown> | null;
  competitors: Array<{
    position: number | null;
    domain: string;
    url: string;
    title: string;
  }> | null;
  top_results: Array<{
    position: number;
    url: string;
    domain: string;
    title: string;
    snippet: string;
    type: string;
  }> | null;
  location_code: number | null;
  device: 'desktop' | 'mobile' | null;
  language_code: string | null;
  engine: 'google' | 'bing' | 'youtube' | null;
  captured_at: string;
}

export interface SerpCheckEntry {
  keyword_id: string;
  location_code: number;
  language_code: string;
  device: 'desktop' | 'mobile';
  engine?: 'google' | 'bing' | 'youtube';
}

export interface SerpCheckConfig {
  id: string;
  project_id: string;
  user_id: string;
  name: string;
  checks: SerpCheckEntry[];
  is_active: boolean;
  schedule_frequency: string | null;
  schedule_day_of_week: number | null;
  schedule_time_of_day: string;
  next_run_at: string | null;
  last_run_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Latest position snapshot per keyword for a specific check config. */
export interface SerpConfigPosition {
  keyword_id: string;
  keyword: string | null;
  position: number | null;
  url: string | null;
  location_code: number | null;
  device: 'desktop' | 'mobile' | null;
  language_code: string | null;
  engine: 'google' | 'bing' | 'youtube' | null;
  captured_at: string | null;
}

export interface PageResult {
  id: string;
  audit_id: string;
  url: string;
  status_code: number;
  title: string | null;
  meta_description: string | null;
  h1: string | null;
  canonical: string | null;
  robots_meta: string | null;
  load_time_ms: number | null;
  page_size_bytes: number | null;
  title_score: number | null;
  meta_score: number | null;
  content_score: number | null;
  technical_score: number | null;
  overall_page_score: number | null;
  issues: Record<string, string[]> | null;
  schema_markup: Record<string, unknown> | unknown[] | null;
  open_graph: Record<string, unknown> | unknown[] | null;
  internal_links: number | null;
  external_links: number | null;
  broken_links: Record<string, unknown> | unknown[] | null;
  lcp: number | null;
  fid: number | null;
  cls: number | null;
  // Phase 2 fields
  word_count: number | null;
  text_ratio: number | null;
  content_language: string | null;
  body_text: string | null;
  meta_keywords: string | null;
  title_length: number | null;
  meta_description_length: number | null;
  h1_count: number | null;
  h2_count: number | null;
  crawl_depth: number | null;
  content_type: string | null;
  is_redirect: boolean;
  final_url: string | null;
  redirect_chain: Record<string, unknown> | null;
  is_indexable: boolean | null;
  indexability_reason: string | null;
  is_in_sitemap: boolean;
  is_https: boolean | null;
  has_hsts: boolean | null;
  has_mixed_content: boolean | null;
  ttfb_ms: number | null;
  heading_score: number | null;
  link_score: number | null;
  image_score: number | null;
  schema_score: number | null;
  was_js_rendered: boolean;
  // Core Web Vitals (measured during crawl)
  lcp_ms: number | null;
  cls_score: number | null;
  fcp_ms: number | null;
  dom_interactive_ms: number | null;
  dom_complete_ms: number | null;
  created_at: string;
  // Phase D fields
  page_type: string | null;
  page_type_confidence: number | null;
  breadcrumbs: Array<{ name: string; url?: string; position?: number }> | null;
  breadcrumb_depth: number | null;
  irys_index: number | null;
  path_segments: string[] | null;
  is_directory_index: boolean | null;
  url_hygiene_flags: Array<{ code: string; severity: string; description: string }> | null;
}

export interface IssueSummary {
  audit_id: string;
  total_pages: number;
  issues: Record<string, number>;
  issue_urls: Record<string, string[]>;
}

export interface PageTreeBreadcrumb {
  name: string;
  url: string;
  position: number;
}

// ── Keyword Map types ─────────────────────────────────────────────────────

export interface KeywordMapOut {
  id: string;
  project_id: string;
  name: string;
  mode: 'algo' | 'ai' | 'manual' | 'mixed';
  source: 'gsc' | 'dataforseo' | 'manual' | 'import';
  status: 'draft' | 'running' | 'completed' | 'failed';
  language_code: string;
  created_at: string;
  updated_at: string;
  entry_count?: number;
  pending_ai_count?: number;
}

export interface KeywordMapCandidateUrl {
  url: string;
  score: number;
}

export interface KeywordMapEntryOut {
  id: string;
  map_id: string;
  keyword: string;
  secondary_keyword?: string;
  tertiary_keyword?: string;
  search_volume?: number;
  secondary_volume?: number;
  tertiary_volume?: number;
  cpc?: number;
  secondary_cpc?: number;
  tertiary_cpc?: number;
  difficulty?: number;
  intent?: string;
  primary_url?: string;
  candidate_urls?: KeywordMapCandidateUrl[];
  score?: number;
  decision_source: 'algo' | 'ai' | 'manual' | 'gsc';
  secondary_source?: string;
  tertiary_source?: string;
  confidence?: number;
  locked: boolean;
  cannibalization_flag: boolean;
  pending_suggestion?: {
    keyword?: string | null;
    secondary_keyword?: string | null;
    tertiary_keyword?: string | null;
    rationale?: string;
    confidence?: number;
  } | null;
  notes?: string;
  refocus_consent?: boolean;
  keyword_id?: string;
  last_seen_at?: string;
  cannibalization_warning?: {
    keyword: string;
    slot: 'primary' | 'secondary';
    other_url: string;
    other_entry_id: string;
    recommended_page: string;
  } | null;
}

export interface KeywordMapEntryListResponse {
  items: KeywordMapEntryOut[];
  total: number;
  page: number;
  per_page: number;
}

export interface KeywordMapImportResult {
  imported: number;
  source: string;
  audit_id?: string;
  total_urls?: number;
  total_gsc_keywords?: number;
  total_input?: number;
}

export interface KeywordMapRunResult {
  task_id: string;
  map_id: string;
  status: string;
}

// ── Keyword Research types (competitor gap / quick wins / LLM) ──────────────

export interface GapKeywordItem {
  keyword: string;
  search_volume: number;
  cpc: number | null;
  competition: number | null;
  difficulty?: number | null;
  position: number | null;
  url: string | null;
  already_tracked: boolean;
  source: string;
  source_group: string;
  source_domain: string;
  source_competitor_id: string;
}

export interface LlmMention {
  platform?: string;
  model_name?: string;
  question: string;
  answer: string | null;
  ai_search_volume: number | null;
  monthly_searches?: number | null;
  sources?: string[] | null;
}

export interface ResearchRunItem {
  keyword: string;
  search_volume: number | null;
  cpc: number | null;
  platform?: string;
  difficulty?: number | null;
  position?: number | null;
  url?: string | null;
  already_tracked?: boolean;
  competition?: number | null;
  [key: string]: unknown;
}

export interface ResearchRunsResponse {
  items: ResearchRunItem[];
  total: number;
  snapshots: Array<{ created_at: string | null; count: number }>;
  run_type: string;
  competitor_id: string | null;
  last_saved_at: string | null;
}

export interface QuickWinsResponse {
  items: Array<{
    keyword: string;
    search_volume: number | null;
    difficulty: number | null;
    intent: string | null;
    position: number | null;
    clicks: number;
    impressions: number;
    ctr: number | null;
    page_url: string | null;
    opportunity: number;
    tracked: boolean;
  }>;
  total: number;
  source: string;
}

export interface LlmResearchResponse {
  items: LlmMention[];
  total: number;
}

export interface EnrichKeywordsResponse {
  enriched: number;
  skipped: number;
  cost_usd: number;
  estimated: boolean;
  completed: boolean;
  candidates?: Array<{
    id: string;
    keyword: string;
    search_volume: number | null;
    difficulty: number | null;
    intent: string | null;
    current_position: number | null;
    impressions: number;
    clicks: number;
    cannibalized: boolean;
  }>;
  message?: string;
}

// ── PageTree types ────────────────────────────────────────────────────────

// ── Crawl Data types ────────────────────────────────────────────────────────

export interface CrawledLink {
  id: string;
  source_page_id: string;
  target_url: string;
  anchor_text?: string;
  link_type: string;
  rel_attributes?: string;
  is_broken?: boolean;
  status_code?: number;
  link_position?: string;
  is_image_link: boolean;
  alt_text?: string;
  is_follow: boolean;
}

export interface HreflangTag {
  id: string;
  page_id: string;
  language: string;
  region?: string;
  href: string;
  is_self_referencing: boolean;
  is_valid: boolean;
  validation_error?: string;
}

export interface SchemaMarkup {
  id: string;
  page_id: string;
  schema_type: string;
  raw_json: Record<string, unknown>;
  format: string;
  is_valid: boolean;
  validation_errors?: Record<string, unknown>;
}

export interface ImageInfo {
  id: string;
  page_id: string;
  src: string;
  alt_text?: string;
  width?: number;
  height?: number;
  file_size_bytes?: number;
  format?: string;
  is_lazy_loaded: boolean;
  has_alt: boolean;
  alt_length?: number;
}

export interface ResourceAsset {
  id: string;
  page_id: string;
  url: string;
  resource_type: string;
  size_bytes?: number;
  load_time_ms?: number;
  is_render_blocking: boolean;
  is_async: boolean;
  is_defer: boolean;
}

export interface DuplicateContent {
  duplicate_type: string;
  duplicate_value: string;
  page_urls: string[];
  page_count: number;
}

export interface CrawlRedirect {
  url: string;
  status_code: number;
  redirect_chain?: string[];
  final_url?: string;
}

export interface CrawlStats {
  total_urls_discovered: number;
  total_urls_crawled: number;
  total_urls_blocked: number;
  total_urls_failed: number;
  total_urls_redirected: number;
  status_2xx_count: number;
  status_3xx_count: number;
  status_4xx_count: number;
  status_5xx_count: number;
  avg_load_time_ms?: number;
  avg_page_size_bytes?: number;
  total_internal_links?: number;
  total_external_links?: number;
  total_broken_links?: number;
  total_images?: number;
  images_missing_alt?: number;
  total_schema_pages?: number;
  total_hreflang_pages?: number;
  unique_pages?: number;
  https_pages?: number;
  non_https_pages?: number;
  avg_crawl_depth?: number;
  pages_with_schema?: number;
}

// ── New Crawl Feature Interfaces ──────────────────────────────────────────

export interface CrawlComparisonMetric {
  metric: string;
  current_value?: number | string | null;
  previous_value?: number | string | null;
  change_pct?: number | null;
  change_absolute?: number | null;
  direction: 'up' | 'down' | 'neutral';
}

export interface CrawlComparisonResponse {
  current_audit_id: string;
  previous_audit_id: string;
  current_date?: string | null;
  previous_date?: string | null;
  metrics: CrawlComparisonMetric[];
}

export interface CWVSummary {
  has_data: boolean;
  total_pages: number;
  pages_with_cwv: number;
  avg_lcp_ms?: number | null;
  avg_cls?: number | null;
  avg_fcp_ms?: number | null;
  avg_ttfb_ms?: number | null;
  lcp_good: number;
  lcp_needs_improvement: number;
  lcp_poor: number;
  cls_good: number;
  cls_needs_improvement: number;
  cls_poor: number;
}

export interface RobotsRule {
  user_agent: string;
  allow_rules: string[];
  disallow_rules: string[];
  crawl_delay?: number | null;
}

export interface RobotsTesterResult {
  found: boolean;
  url?: string | null;
  content?: string | null;
  status_code?: number | null;
  rules: RobotsRule[];
  sitemap_urls: string[];
  test_results?: Array<{ path: string; allowed: boolean; blocked_by?: string | null }> | null;
  issues: Array<{ type: string; message: string }>;
}

export interface SitemapUrl {
  loc: string;
  lastmod?: string | null;
  changefreq?: string | null;
  priority?: number | null;
}

export interface SitemapAnalysisResult {
  found: boolean;
  sitemap_url?: string | null;
  total_urls: number;
  urls: SitemapUrl[];
  in_sitemap_not_crawled: number;
  crawled_not_in_sitemap: number;
  in_both: number;
  issues: Array<{ type: string; message: string }>;
}

export interface MobileFriendlyResult {
  url: string;
  has_viewport_meta: boolean;
  viewport_content?: string | null;
  has_mobile_issues: boolean;
  issues: Array<{ type: string; message: string }>;
  tap_target_warnings: number;
  font_size_warnings: number;
}

export interface MobileFriendlySummary {
  total_pages_checked: number;
  mobile_friendly: number;
  has_issues: number;
  missing_viewport: number;
  issues_by_type: Record<string, number>;
  page_results: MobileFriendlyResult[];
}

export interface SchemaValidationIssue {
  field: string;
  error: string;
  severity: 'error' | 'warning';
}

export interface SchemaValidationResult {
  schema_id: string;
  schema_type: string;
  format: string;
  is_valid: boolean;
  issues: SchemaValidationIssue[];
  warnings: SchemaValidationIssue[];
}

export interface SchemaValidationSummary {
  total_schemas: number;
  valid_schemas: number;
  invalid_schemas: number;
  schemas_by_type: Record<string, number>;
  results: SchemaValidationResult[];
}

// ── Page tree ─────────────────────────────────────────────────────────────

export interface PageTreeNode {
  id: string;
  url: string;
  title?: string;
  overall_page_score?: number;
  is_directory_index?: boolean;
  crawl_depth?: number;
  /** Semantic depth inferred from breadcrumb trail. Preferred signal for
   *  flat-URL shops (JTL, etc.) where URL depth does not reflect content hierarchy. */
  breadcrumb_depth?: number;
  breadcrumbs: PageTreeBreadcrumb[];
  page_type?: string;
  status_code?: number;
  children: PageTreeNode[];
}

export interface PageTreeResponse {
  audit_id: string;
  total_nodes: number;
  has_breadcrumb_hierarchy: boolean;
  pages_with_breadcrumbs: number;
  max_crawl_depth: number;
  max_breadcrumb_depth: number;
  tree: PageTreeNode[];
}

export interface AuditStats {
  total_pages: number;
  score_distribution: Record<string, number>;
  status_codes: Record<string, number>;
  avg_scores: Record<string, number | null>;
  performance: {
    avg_load_time: number | null;
    min_load_time: number | null;
    max_load_time: number | null;
    avg_page_size: number | null;
    avg_ttfb: number | null;
  };
  indexability: { indexable: number; not_indexable: number; unknown: number };
  security: { https: number; http: number };
  content: { has_schema: number; has_open_graph: number; redirects: number; in_sitemap: number };
  issue_severity: { critical: number; warning: number; info: number };
  top_issues: Array<{ issue: string; count: number; urls: string[] }>;
}

export interface PageFilterConfig {
  status_code?: number | null;
  is_indexable?: boolean | null;
  score_min?: number | null;
  score_max?: number | null;
  has_issues?: boolean | null;
  is_https?: boolean | null;
  is_redirect?: boolean | null;
  page_type?: string | null;
  search?: string;
  sort_by?: string;
  sort_dir?: string;
}

export interface FilterPreset {
  id: string;
  user_id: string;
  name: string;
  filter_config: PageFilterConfig;
  created_at: string;
  updated_at: string;
}

export interface BacklinkData {
  id: string;
  project_id: string;
  source_url: string;
  target_url: string;
  anchor_text: string | null;
  link_type: string | null;
  domain_authority: number | null;
  status: 'active' | 'lost' | 'broken';
  first_seen: string;
  last_checked: string;
  last_seen: string | null;
}

export interface DataForSeoUsage {
  configured: boolean;
  error?: string;
  login?: string | null;
  timezone?: string | null;
  total?: number | null;
  balance?: number | null;
  spent?: number | null;
  backlinks_subscription_expiry_date?: string | null;
  llm_mentions_subscription_expiry_date?: string | null;
}

export interface CompetitorData {
  id: string;
  project_id: string;
  domain: string;
  name: string | null;
  source: string | null;
  organic_keywords: number | null;
  organic_traffic: number | null;
  domain_authority: number | null;
  overlap_score: number | null;
  total_backlinks: number | null;
  referring_domains: number | null;
  created_at: string;
}

export interface GscPageMetric {
  id: string;
  page_url: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
  date_from: string | null;
  date_to: string | null;
}

export interface GscKeywordData {
  keyword: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
  page_url: string | null;
}

export interface GscKeywordPaginated {
  items: GscKeywordData[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

export interface GscSummary {
  total_clicks: number;
  total_impressions: number;
  avg_position: number;
  avg_ctr: number;
  pages_with_gsc_data: number;
  top_pages: GscPageMetric[];
  top_keywords: GscKeywordData[];
}

export interface ProjectStats {
  project_id: string;
  seo_score: number | null;
  organic_traffic_30d: number | null;
}

// ── AI Overview Interfaces ──────────────────────────────────────────

export interface AIOverviewTracking {
  id: string;
  project_id: string;
  keyword_id: string | null;
  audit_id: string | null;
  keyword_text: string;
  ai_overview_present: boolean;
  ai_overview_position: number | null;
  domain_cited: boolean;
  cited_url: string | null;
  cited_snippet: string | null;
  ai_overview_markdown: string | null;
  all_cited_domains: string[] | null;
  reference_count: number;
  checked_at: string;
}

export interface AIOverviewSummary {
  total_keywords_checked: number;
  ai_overview_present_count: number;
  domain_cited_count: number;
  citation_rate: number;
  last_checked: string | null;
}

// ── Page Type Stats ─────────────────────────────────────────────────

export interface PageTypeStats {
  page_type: string;
  count: number;
  representative_url: string | null;
}

// ── Page Engagement (GA4) Interfaces ────────────────────────────────

export interface PageEngagementData {
  id: string;
  audit_id: string;
  project_id: string;
  page_url: string;
  avg_session_duration_seconds: number | null;
  engagement_rate: number | null;
  bounce_rate: number | null;
  page_views: number | null;
  active_users: number | null;
  avg_engagement_time_seconds: number | null;
  sessions: number | null;
  date_range_start: string | null;
  date_range_end: string | null;
  synced_at: string;
}

export interface EngagementSummary {
  audit_id: string;
  total_pages_with_data: number;
  avg_session_duration_seconds: number;
  avg_engagement_rate: number;
  avg_bounce_rate: number;
  total_page_views: number;
  total_active_users: number;
  total_sessions: number;
}

export interface PageKeyword {
  id: string;
  page_id: string;
  audit_id: string;
  keyword: string;
  tf: number;
  df: number;
  wdf: number;
  idf: number;
  wdf_idf: number;
  in_title: boolean;
  in_h1: boolean;
  in_meta_keywords: boolean;
}

export interface AuditHistoryEntry {
  id: string;
  audit_type: string;
  status: string;
  overall_score: number | null;
  pages_crawled: number;
  total_issues: number;
  issue_severity: { critical: number; warning: number; info: number };
  avg_scores: { title: number | null; meta: number | null; content: number | null; technical: number | null };
  created_at: string;
  completed_at: string | null;
}

export interface ScoreTrendPoint {
  audit_id: string;
  date: string;
  overall_score: number | null;
  pages_crawled: number;
  title_avg: number | null;
  meta_avg: number | null;
  content_avg: number | null;
  technical_avg: number | null;
}

export interface IssueTrendPoint {
  audit_id: string;
  date: string;
  critical: number;
  warning: number;
  info: number;
  total: number;
}

export interface KeywordTrendPoint {
  date: string;
  audit_id: string;
  position: number;
  clicks: number;
  impressions: number;
}

export interface KeywordTrend {
  keyword: string;
  page_url: string;
  current_position: number;
  current_clicks: number;
  current_impressions: number;
  trend: KeywordTrendPoint[];
  position_change: number | null;
}

export interface CrawlHealthSnapshot {
  total_pages: number;
  indexable_pages: number;
  non_indexable_pages: number;
  https_pages: number;
  http_pages: number;
  avg_load_time_ms: number | null;
  avg_word_count: number | null;
  avg_page_size_kb: number | null;
  pages_with_issues: number;
  redirect_count: number;
  status_2xx: number;
  status_3xx: number;
  status_4xx: number;
  status_5xx: number;
}

export interface TopWdfIdfKeyword {
  keyword: string;
  wdf_idf: number;
  tf: number;
  df: number;
  page_count: number;
  in_title: boolean;
  in_h1: boolean;
  page_url: string;
}

export interface TopIssueSummary {
  issue_type: string;
  count: number;
  severity: string;
}

export interface SeoDashboardResponse {
  project_id: string;
  domain: string;
  total_audits: number;
  last_audit_date: string | null;
  current_score: number | null;
  score_trend: ScoreTrendPoint[];
  issue_trend: IssueTrendPoint[];
  top_keywords: KeywordTrend[];
  total_keywords_tracked: number;
  total_pages_tracked: number;
  crawl_health: CrawlHealthSnapshot | null;
  wdf_idf_keywords: TopWdfIdfKeyword[];
  top_issues: TopIssueSummary[];
  score_breakdown: Record<string, number | null> | null;
}

export interface SeoTask {
  id: string;
  project_id: string;
  audit_id: string | null;
  source: string;
  category: string;
  severity: 'info' | 'warn' | 'error';
  title: string;
  description: string | null;
  target_url: string | null;
  target_field: string | null;
  evidence: Record<string, unknown> | null;
  status: 'open' | 'in_progress' | 'done' | 'dismissed' | 'verifying' | 'scheduled' | 'deferred';
  verification_method: string | null;
  scheduled_for: string | null;
  effort: 'quick' | 'medium' | 'large' | null;
  impact: 'high' | 'medium' | 'low' | null;
  points: number | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  verified_at: string | null;
}

export interface SeoGamificationState {
  project_id: string;
  points: number;
  streak: number;
  best_streak: number;
  last_action_date: string | null;
  level: number;
}

export interface SeoTaskListResponse {
  items: SeoTask[];
  total: number;
  page: number;
  per_page: number;
}

export interface SeoTaskListOptions {
  status?: string;
  source?: string;
  severity?: string;
  category?: string;
  page?: number;
  perPage?: number;
}

// ── Phase 3: Content Optimization Workbench types ────────────────────────────

export interface OptimizationCandidate {
  url: string;
  page_id: string;
  opportunity_score: number;
  tier: 1 | 2 | 3 | 4;
  content_score: number | null;
  serp_position: number | null;
  keyword_volume: number | null;
  word_count: number | null;
  page_type: string | null;
  cannibalization_flag: boolean;
}

export interface OptimizationPreset {
  id: string;
  project_id: string | null;
  name: string;
  page_type: string;
  is_builtin: boolean;
  elements: Array<{ name: string; priority: 'required' | 'recommended' | 'optional' }> | null;
  heading_blueprint: Record<string, { count?: number; min?: number; max?: number; min_chars?: number; max_chars?: number }> | null;
  length_blueprint: { min_words?: number; min_chars?: number } | null;
  schema_types: { primary?: string[]; secondary?: string[] } | null;
  structure: { sections: Array<{ stype: string; label: string; words: number }> } | null;
  brief_settings: { tonality?: string; instructions?: string } | null;
  created_at: string;
  updated_at: string;
}

export interface OptimizationProject {
  id: string;
  project_id: string;
  preset_id: string | null;
  name: string;
  status: 'draft' | 'in_progress' | 'review' | 'done' | 'archived';
  target_url: string | null;
  page_type: string | null;
  language: string | null;
  keywords: { focus_keyword?: string; secondary_keywords?: string[]; tertiary_keywords?: string[]; excluded_keywords?: string[] } | null;
  structure: { sections?: Array<{ stype: string; label: string; words: number }> } | null;
  brief_settings: { tonality?: string; instructions?: string } | null;
  serp_analysis: SerpAnalysisResult | null;
  dataforseo_consented: boolean;
  content_brief: Record<string, unknown> | null;
  brief_generated_at: string | null;
  score_before: number | null;
  score_after: number | null;
  geo_aeo_score_before: number | null;
  geo_aeo_score_after: number | null;
  opportunity_score: number | null;
  outline: Record<string, unknown> | null;
  content_source: 'scraped' | 'scratch' | null;
  quality_report: Record<string, unknown> | null;
  verify_flags: string[] | null;
  iteration_count: number;
  max_iterations: number;
  last_feedback: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface OptimizationIteration {
  id: string;
  optimization_project_id: string;
  iteration: number;
  phase: 'outline' | 'draft' | 'critique' | 'fix' | 'finalize' | 'serp';
  status: 'running' | 'approved' | 'rejected' | 'max_reached' | 'error' | 'done';
  model: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  critique: { approved?: boolean; checks?: Array<{ metric: string; status: string; current: number | null; target: Record<string, unknown> | null; feedback: string }>; feedback?: string } | null;
  scores: { overall?: number; previous?: number; geo_aeo?: number } | null;
  feedback: string | null;
  created_at: string;
}

export interface GenerationStep {
  id: string;
  optimization_project_id: string;
  step_order: number;
  section_id: string;
  heading_level: string | null;
  heading_text: string | null;
  status: 'pending' | 'generating' | 'done' | 'rejected' | 'regenerating' | 'needs_review';
  content_html: string | null;
  word_count: number | null;
  section_score: number | null;
  verify_flags: string[] | null;
  notes: string[] | null;
  ai_model: string | null;
  created_at: string;
  accepted_at: string | null;
}

export interface ContentScoreResult {
  overall_score: number | null;
  geo_aeo_score: number | null;
  reference_score?: {
    total_score: number;
    seo_score: number;
    geo_score: number;
    publish_ready: boolean;
    seo_checks: ContentQualityCheck[];
    geo_checks: ContentQualityCheck[];
    recommendations: string[];
    stats: Record<string, number>;
  };
  details: {
    element_results?: Array<{ element: string; priority: string; present: boolean; score: number }>;
    heading_score?: number;
    length_score?: number;
    keyword_score?: number;
    schema_score?: number;
    missing_required_count?: number;
    geo_aeo_signals?: Record<string, number>;
    content_quality?: ContentQualityResult;
  };
}

export interface ContentQualityResult {
  overall_score: number;
  content_quality_score: number;
  seo_score: number;
  total_points: number;
  max_points: number;
  ready_for_publish: boolean;
  checks: ContentQualityCheck[];
  recommendations: string[];
  reference_score?: Record<string, unknown>;
  similarity_analysis?: {
    similarity_score: number | null;
    seo_improvement_score: number | null;
    content_quality_score: number | null;
    key_differences: string[];
    strengths: string[];
    weaknesses: string[];
    recommendations: string[];
  };
  term_targets?: {
    terms: Array<{ term: string; target: number; category: 'must_have' | 'recommended' | 'supplementary'; tier: string; source: string }>;
    sources?: Record<string, unknown>;
  };
  meta_tags?: {
    title: string;
    description: string;
    canonical_url: string;
    robots: string;
    og_title: string;
    og_description: string;
  };
  stats: {
    word_count: number;
    target_word_count: number;
    para_count: number;
    avg_para_words: number;
    h1_count: number;
    h2_count: number;
    h3_count: number;
    list_count: number;
    internal_links: number;
    external_links: number;
    images: number;
    images_with_alt: number;
    focus_keyword_count: number;
    focus_keyword_density: number;
    secondary_keywords_found: number;
    secondary_keywords_total: number;
    keyword_frequency: Array<{ keyword: string; tier: 'focus' | 'secondary' | 'tertiary'; count: number; density: number }>;
  };
}

export interface ContentQualityCheck {
  key: string;
  description: string;
  status: 'pass' | 'warn' | 'fail';
  points: number;
  max_points: number;
}

export interface SerpAnalysisResult {
  focus_keyword: string;
  competitors: Array<{
    position: number | null;
    url: string;
    domain: string;
    title: string;
    keyword_hits: Array<{ keyword: string; count: number }>;
  }>;
  keyword_frequency: Array<{ keyword: string; tier: 'focus' | 'secondary'; occurrences: number; in_results: number }>;
  competitor_pages?: Array<{
    url: string;
    domain: string;
    title: string;
    word_count: number;
    keyword_hits: Array<{ keyword: string; count: number }>;
  }>;
  competitor_page_keyword_frequency?: Array<{ keyword: string; tier: 'focus' | 'secondary'; occurrences: number; in_pages: number }>;
  keyword_gaps?: Array<{ keyword: string; competitor_count: number; search_volume: number; best_rank: number }>;
  competitor_keywords?: Array<{ keyword: string; competitor_count: number; search_volume: number; best_rank: number; domains: string[] }>;
  opportunities?: Array<{ keyword: string; competitor_count: number; search_volume: number; best_rank: number }>;
  easy_wins?: Array<{ keyword: string; competitor_count: number; search_volume: number; best_rank: number }>;
  long_tail?: Array<{ keyword: string; competitor_count: number; search_volume: number; best_rank: number }>;
  high_value?: Array<{ keyword: string; competitor_count: number; search_volume: number; best_rank: number }>;
  avg_competitor_word_count?: number;
  serp_features?: Record<string, boolean>;
  consent_required?: boolean;
  error: string | null;
}

export const seoAPI = {
  // Projects
  getProjects: async (apiKey: string | null): Promise<SeoProject[]> => {
    return apiCall<SeoProject[]>(`${SEO_SERVICE_URL}/api/projects`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  createProject: async (apiKey: string | null, data: {
    name: string;
    domain: string;
    description?: string;
    primary_language?: string;
    location_code?: number;
    url_structure?: 'single_language' | 'subdirectory' | 'subdomain';
    language_paths?: Record<string, string> | null;
    crawl_scope_pattern?: string | null;
  }): Promise<SeoProject> => {
    return apiCall<SeoProject>(`${SEO_SERVICE_URL}/api/projects`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  getProject: async (apiKey: string | null, projectId: string): Promise<SeoProject> => {
    return apiCall<SeoProject>(`${SEO_SERVICE_URL}/api/projects/${projectId}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  updateProject: async (apiKey: string | null, projectId: string, data: Partial<SeoProject>): Promise<SeoProject> => {
    return apiCall<SeoProject>(`${SEO_SERVICE_URL}/api/projects/${projectId}`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },



  createAudit: async (
    apiKey: string | null,
    data: { project_id: string; audit_type: string; target_domain?: string | null; crawl_settings?: AuditCrawlSettings | null }
  ): Promise<Audit> => {
    return apiCall<Audit>(`${SEO_SERVICE_URL}/api/audits`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  triggerAudit: async (apiKey: string | null, auditId: string): Promise<Audit> => {
    return apiCall<Audit>(`${SEO_SERVICE_URL}/api/audits/${auditId}/start`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  updateAudit: async (
    apiKey: string | null,
    auditId: string,
    data: { audit_type?: string; crawl_settings?: AuditCrawlSettings | null }
  ): Promise<Audit> => {
    return apiCall<Audit>(`${SEO_SERVICE_URL}/api/audits/${auditId}`, {
      method: 'PATCH',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  getProjectsBulkStats: async (apiKey: string | null, projectIds: string[]): Promise<ProjectStats[]> => {
    if (!projectIds.length) return [];
    return apiCall<ProjectStats[]>(
      `${SEO_SERVICE_URL}/api/projects/stats/batch?project_ids=${projectIds.join(',')}`,
      { headers: { 'X-API-Key': apiKey ?? '' } },
    );
  },

  getAudits: async (apiKey: string | null, projectId: string): Promise<Audit[]> => {
    return apiCall<Audit[]>(`${SEO_SERVICE_URL}/api/audits?project_id=${projectId}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getAuditResults: async (apiKey: string | null, auditId: string): Promise<Audit> => {
    return apiCall<Audit>(`${SEO_SERVICE_URL}/api/audits/${auditId}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  // Keywords
  getKeywords: async (apiKey: string | null, projectId: string, perPage: number = 500): Promise<Keyword[]> => {
    return apiCall<Keyword[]>(`${SEO_SERVICE_URL}/api/keywords?project_id=${projectId}&per_page=${perPage}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  addKeyword: async (apiKey: string | null, projectId: string, keyword: string): Promise<Keyword> => {
    return apiCall<Keyword>(`${SEO_SERVICE_URL}/api/keywords`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify({ project_id: projectId, keyword }),
    });
  },


  updateKeyword: async (
    apiKey: string | null,
    keywordId: string,
    data: Partial<Pick<Keyword, 'tags' | 'priority' | 'intent' | 'is_branded' | 'target_url' | 'notes' | 'url'>>,
  ): Promise<Keyword> => {
    return apiCall<Keyword>(`${SEO_SERVICE_URL}/api/keywords/${keywordId}`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  queryKeywords: async (
    apiKey: string | null,
    projectId: string,
    params: {
      scope?: 'master' | `competitor:${string}` | 'all';
      search?: string;
      priority?: string;
      intent?: string;
      tag?: string;
      source_group?: string;
      discovery_status?: string;
      review_status?: string;
      position_min?: number;
      position_max?: number;
      difficulty_min?: number;
      difficulty_max?: number;
      page?: number;
      per_page?: number;
      sort_by?: 'keyword' | 'current_position' | 'search_volume' | 'difficulty' | 'updated_at';
      sort_dir?: 'asc' | 'desc';
    } = {},
  ): Promise<KeywordQueryResponse> => {
    const qs = new URLSearchParams({ project_id: projectId });
    const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
    for (const [k, v] of entries) qs.set(k, String(v));
    return apiCall<KeywordQueryResponse>(`${SEO_SERVICE_URL}/api/keywords/query?${qs}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getKeywordsByUrl: async (
    apiKey: string | null,
    projectId: string,
    params: { search?: string; limit?: number } = {},
  ): Promise<KeywordByUrlResponse> => {
    const qs = new URLSearchParams({ project_id: projectId });
    if (params.search) qs.set('search', params.search);
    if (params.limit) qs.set('limit', String(params.limit));
    return apiCall<KeywordByUrlResponse>(`${SEO_SERVICE_URL}/api/keywords/by-url?${qs}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getKeywordReviewQueue: async (apiKey: string | null, projectId: string, limit: number = 200): Promise<Keyword[]> => {
    return apiCall<Keyword[]>(`${SEO_SERVICE_URL}/api/keywords/review-queue?project_id=${projectId}&limit=${limit}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  reviewKeyword: async (apiKey: string | null, keywordId: string, action: 'accept' | 'ignore'): Promise<Keyword> => {
    return apiCall<Keyword>(`${SEO_SERVICE_URL}/api/keywords/${keywordId}/review`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify({ action }),
    });
  },


  checkKeywordRanking: async (apiKey: string | null, keywordId: string): Promise<{ task_id: string }> => {
    return apiCall<{ task_id: string }>(`${SEO_SERVICE_URL}/api/keywords/${keywordId}/check`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getKeywordSuggestions: async (
    apiKey: string | null,
    projectId: string,
    auditId?: string,
    limit: number = 20
  ): Promise<Array<{
    keyword: string;
    wdf_idf_score: number;
    page_count: number;
    in_title: boolean;
    in_h1: boolean;
    search_volume: number | null;
    difficulty: number | null;
  }>> => {
    const params = new URLSearchParams({ project_id: projectId, limit: String(limit) });
    if (auditId) params.set('audit_id', auditId);
    return apiCall(`${SEO_SERVICE_URL}/api/keywords/suggestions?${params}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  keywordResearch: async (
    apiKey: string | null,
    projectId: string,
    mode: 'suggestions' | 'ideas' | 'ranked',
    input: string[],
    limit?: number
  ): Promise<Array<{
    keyword: string;
    search_volume: number | null;
    cpc: number | null;
    competition: number | null;
    categories: number[] | null;
    serp_features: string[] | null;
  }>> => {
    return apiCall(`${SEO_SERVICE_URL}/api/keywords/research?project_id=${encodeURIComponent(projectId)}`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify({ mode, input, limit: limit ?? 50 }),
    });
  },


  pullGscLive: async (apiKey: string | null, projectId: string): Promise<{
    audit_id: string;
    pages: number;
    keywords: number;
    history: number;
    synced_tracked: number;
    message: string;
  }> => {
    return apiCall(`${SEO_SERVICE_URL}/api/keywords/gsc-pull?project_id=${projectId}`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getKeywordPages: async (apiKey: string | null, keywordId: string): Promise<{
    pages: Array<{
      url: string;
      position: number | null;
      clicks: number;
      impressions: number;
      ctr: number | null;
      source: string;
      wdf_idf?: number | null;
      word_count?: number | null;
      in_title?: boolean | null;
      in_h1?: boolean | null;
    }>;
    total: number;
    audit_id?: string | null;
    fallback?: string | null;
  }> => {
    return apiCall(`${SEO_SERVICE_URL}/api/keywords/${keywordId}/pages`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },



  importKeywordsWithMetadata: async (
    apiKey: string | null,
    projectId: string,
    keywords: Array<{
      keyword: string;
      search_volume?: number | null;
      difficulty?: number | null;
      competition?: number | null;
      position?: number | null;
      url?: string | null;
      source?: string;
      source_group?: string | null;
      source_domain?: string | null;
      source_competitor_id?: string | null;
      source_audit_id?: string | null;
    }>
  ): Promise<{
    imported: number;
    skipped: number;
    keywords: Array<{ id: string; keyword: string; search_volume: number | null; current_position: number | null }>;
  }> => {
    return apiCall(`${SEO_SERVICE_URL}/api/keywords/import`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify({ project_id: projectId, keywords }),
    });
  },

  discoverKeywordsCompetitor: async (
    apiKey: string | null,
    projectId: string,
    competitorId: string,
    filters?: {
      limit?: number;
      min_volume?: number;
      max_position?: number;
      position_min?: number;
      position_max?: number;
      min_difficulty?: number;
      max_difficulty?: number;
      min_cpc?: number;
      max_cpc?: number;
    }
  ): Promise<{
    items: Array<{
      keyword: string;
      search_volume: number;
      cpc: number | null;
      competition: number | null;
      position: number | null;
      url: string | null;
      already_tracked: boolean;
      source: string;
      source_group: string;
      source_domain: string;
      source_competitor_id: string;
    }>;
    total: number;
    competitor_domain: string;
    competitor_id: string;
  }> => {
    const params = new URLSearchParams({ project_id: projectId, competitor_id: competitorId });
    if (filters?.limit) params.set('limit', String(filters.limit));
    if (filters?.min_volume) params.set('min_volume', String(filters.min_volume));
    if (filters?.max_position) params.set('max_position', String(filters.max_position));
    if (filters?.position_min) params.set('position_min', String(filters.position_min));
    if (filters?.position_max) params.set('position_max', String(filters.position_max));
    if (filters?.min_difficulty) params.set('min_difficulty', String(filters.min_difficulty));
    if (filters?.max_difficulty) params.set('max_difficulty', String(filters.max_difficulty));
    if (filters?.min_cpc) params.set('min_cpc', String(filters.min_cpc));
    if (filters?.max_cpc) params.set('max_cpc', String(filters.max_cpc));
    return apiCall(`${SEO_SERVICE_URL}/api/keywords/competitor-discover?${params}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  // ── Keyword Research: saved-run history, quick wins, LLM research ────────
  getResearchRuns: async (
    apiKey: string | null,
    projectId: string,
    runType: 'gap' | 'opportunities' | 'llm',
    snapshot?: string,
    competitorId?: string,
    limit: number = 500,
  ): Promise<ResearchRunsResponse> => {
    return apiCall<ResearchRunsResponse>(`${SEO_SERVICE_URL}/api/keywords/research-runs`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '', 'Content-Type': 'application/json' },
      body: JSON.stringify({
        project_id: projectId,
        run_type: runType,
        snapshot: snapshot ?? undefined,
        competitor_id: competitorId ?? undefined,
        limit,
      }),
    });
  },

  getQuickWins: async (
    apiKey: string | null,
    projectId: string,
    filters?: { position_min?: number; position_max?: number; limit?: number; min_impressions?: number }
  ): Promise<QuickWinsResponse> => {
    const params = new URLSearchParams({ project_id: projectId });
    if (filters?.position_min) params.set('position_min', String(filters.position_min));
    if (filters?.position_max) params.set('position_max', String(filters.position_max));
    if (filters?.limit) params.set('limit', String(filters.limit));
    if (filters?.min_impressions) params.set('min_impressions', String(filters.min_impressions));
    return apiCall<QuickWinsResponse>(`${SEO_SERVICE_URL}/api/keywords/quick-wins?${params}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  llmResearch: async (apiKey: string | null, projectId: string, limit: number = 50): Promise<LlmResearchResponse> => {
    return apiCall<LlmResearchResponse>(`${SEO_SERVICE_URL}/api/keywords/llm-research`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '', 'Content-Type': 'application/json' },
      body: JSON.stringify({ project_id: projectId, limit }),
    });
  },

  enrichKeywords: async (
    apiKey: string | null,
    projectId: string,
    data: {
      scope: string;
      limit?: number;
      consented: boolean;
      keyword_ids?: string[];
    }
  ): Promise<EnrichKeywordsResponse> => {
    return apiCall<EnrichKeywordsResponse>(`${SEO_SERVICE_URL}/api/keywords/enrich`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '', 'Content-Type': 'application/json' },
      body: JSON.stringify({ project_id: projectId, ...data }),
    });
  },

  competitorPull: async (
    apiKey: string | null,
    body: {
      project_id: string;
      competitor_id: string;
      mode?: 'domain' | 'url_list';
      limit?: number;
      position_min?: number;
      position_max?: number;
      min_volume?: number;
      url_list?: string[];
      keywords_per_url?: number;
    }
  ): Promise<{
    items: Array<{
      keyword: string;
      search_volume: number | null;
      cpc: number | null;
      competition: number | null;
      position: number | null;
      url: string | null;
      source: string;
      source_group: string;
      source_domain: string;
      source_competitor_id: string;
    }>;
    total: number;
    competitor_domain: string;
    competitor_id: string;
    mode: string;
  }> => {
    return apiCall(`${SEO_SERVICE_URL}/api/keywords/competitor-pull`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(body),
    });
  },

  // SERP
  getSerpHistory: async (apiKey: string | null, keywordId: string): Promise<SerpSnapshot[]> => {
    return apiCall<SerpSnapshot[]>(`${SEO_SERVICE_URL}/api/serp/history?keyword_id=${keywordId}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getConfigPositions: async (apiKey: string | null, configId: string): Promise<SerpConfigPosition[]> => {
    return apiCall<SerpConfigPosition[]>(`${SEO_SERVICE_URL}/api/serp/config-positions?config_id=${configId}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getRankingDistribution: async (apiKey: string | null, projectId: string): Promise<{
    '1_3': number;
    '4_10': number;
    '11_20': number;
    '21_50': number;
    '51_plus': number;
    not_ranked: number;
  }> => {
    return apiCall(`${SEO_SERVICE_URL}/api/serp/ranking-distribution?project_id=${encodeURIComponent(projectId)}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  triggerSerpCheck: async (
    apiKey: string | null,
    keywordId: string,
    opts?: { location_code?: number; device?: 'desktop' | 'mobile'; language_code?: string; engine?: 'google' | 'bing' | 'youtube' },
  ): Promise<SerpSnapshot> => {
    return apiCall<SerpSnapshot>(`${SEO_SERVICE_URL}/api/serp/check`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify({ keyword_id: keywordId, ...opts }),
    });
  },

  getSerpConfigs: async (apiKey: string | null, projectId: string): Promise<SerpCheckConfig[]> => {
    return apiCall<SerpCheckConfig[]>(
      `${SEO_SERVICE_URL}/api/serp/configs?project_id=${encodeURIComponent(projectId)}`,
      { headers: { 'X-API-Key': apiKey ?? '' } },
    );
  },

  createSerpConfig: async (
    apiKey: string | null,
    payload: {
      project_id: string;
      name: string;
      checks: SerpCheckEntry[];
      is_active?: boolean;
      schedule_frequency?: string | null;
      schedule_day_of_week?: number | null;
      schedule_time_of_day?: string;
    },
  ): Promise<SerpCheckConfig> => {
    return apiCall<SerpCheckConfig>(`${SEO_SERVICE_URL}/api/serp/configs`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(payload),
    });
  },

  updateSerpConfig: async (
    apiKey: string | null,
    configId: string,
    payload: Partial<{
      name: string;
      checks: SerpCheckEntry[];
      is_active: boolean;
      schedule_frequency: string | null;
      schedule_day_of_week: number | null;
      schedule_time_of_day: string;
    }>,
  ): Promise<SerpCheckConfig> => {
    return apiCall<SerpCheckConfig>(`${SEO_SERVICE_URL}/api/serp/configs/${configId}`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(payload),
    });
  },

  deleteSerpConfig: async (apiKey: string | null, configId: string): Promise<void> => {
    await apiCall<void>(`${SEO_SERVICE_URL}/api/serp/configs/${configId}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  runSerpConfig: async (apiKey: string | null, configId: string): Promise<{ config_id: string; status: string; checks: number }> => {
    return apiCall(`${SEO_SERVICE_URL}/api/serp/configs/${configId}/run`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  // Backlinks
  getBacklinks: async (
    apiKey: string | null,
    projectId: string,
    page: number = 1,
    perPage: number = 50,
    opts?: {
      competitorId?: string;
      minAuthority?: number;
      linkType?: string;
      status?: string;
    }
  ): Promise<{
    items: BacklinkData[];
    total: number;
    page: number;
    per_page: number;
  }> => {
    const params = new URLSearchParams({
      project_id: projectId,
      page: String(page),
      per_page: String(perPage),
    });
    if (opts?.competitorId) params.set('competitor_id', opts.competitorId);
    if (opts?.minAuthority !== undefined) params.set('min_authority', String(opts.minAuthority));
    if (opts?.linkType) params.set('link_type', opts.linkType);
    if (opts?.status) params.set('status', opts.status);
    return apiCall(`${SEO_SERVICE_URL}/api/backlinks?${params}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  fetchBacklinks: async (
    apiKey: string | null,
    projectId: string,
    limit: number = 100,
    opts?: { competitorId?: string; minAuthority?: number }
  ): Promise<{
    items: BacklinkData[];
    total_fetched: number;
    summary: {
      total_backlinks: number;
      referring_domains: number;
      dofollow: number;
      nofollow: number;
      broken: number;
    };
    cost_usd: number;
    last_synced_at: string;
  }> => {
    return apiCall(`${SEO_SERVICE_URL}/api/backlinks/fetch`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify({
        project_id: projectId,
        limit,
        competitor_id: opts?.competitorId ?? null,
        min_authority: opts?.minAuthority ?? null,
      }),
    });
  },

  getBacklinkSummary: async (
    apiKey: string | null,
    projectId: string,
    competitorId?: string
  ): Promise<{
    project_id: string;
    competitor_id: string | null;
    domain: string;
    total_backlinks: number;
    active_backlinks: number;
    dofollow_links: number;
    nofollow_links: number;
    referring_domains: number;
    avg_domain_authority: number | null;
    stored_backlinks: number;
    last_fetched: string | null;
    last_fetch_cost: number | null;
  }> => {
    const params = new URLSearchParams({ project_id: projectId });
    if (competitorId) params.set('competitor_id', competitorId);
    return apiCall(`${SEO_SERVICE_URL}/api/backlinks/summary?${params}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getBacklinkCompare: async (
    apiKey: string | null,
    projectId: string,
    competitorIds: string[]
  ): Promise<{
    rows: Array<{
      label: string;
      domain: string;
      competitor_id: string | null;
      total_backlinks: number | null;
      referring_domains: number | null;
      dofollow: number | null;
      nofollow: number | null;
      avg_authority: number | null;
      new_backlinks: number | null;
      lost_backlinks: number | null;
      stored_backlinks: number;
      last_fetched: string | null;
    }>;
  }> => {
    const params = new URLSearchParams({ project_id: projectId });
    if (competitorIds.length > 0) params.set('competitor_ids', competitorIds.join(','));
    return apiCall(`${SEO_SERVICE_URL}/api/backlinks/compare?${params}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getReferringDomains: async (apiKey: string | null, projectId: string, limit: number = 50, competitorId?: string): Promise<{
    items: Array<{ domain: string; rank: number | null; backlinks: number | null; dofollow: number | null; first_seen: string | null }>;
    total: number;
  }> => {
    const params = new URLSearchParams({ project_id: projectId, limit: String(limit) });
    if (competitorId) params.set('competitor_id', competitorId);
    return apiCall(`${SEO_SERVICE_URL}/api/backlinks/referring-domains?${params}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getAnchorDistribution: async (apiKey: string | null, projectId: string, limit: number = 30, competitorId?: string): Promise<{
    items: Array<{ anchor: string; backlinks: number; referring_domains: number; first_seen: string | null }>;
  }> => {
    const params = new URLSearchParams({ project_id: projectId, limit: String(limit) });
    if (competitorId) params.set('competitor_id', competitorId);
    return apiCall(`${SEO_SERVICE_URL}/api/backlinks/anchors?${params}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getTopLinkedPages: async (apiKey: string | null, projectId: string, limit: number = 20, competitorId?: string): Promise<{
    items: Array<{ page_url: string; backlinks: number; referring_domains: number; dofollow: number | boolean }>;
  }> => {
    const params = new URLSearchParams({ project_id: projectId, limit: String(limit) });
    if (competitorId) params.set('competitor_id', competitorId);
    return apiCall(`${SEO_SERVICE_URL}/api/backlinks/top-pages?${params}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getBacklinkHistory: async (apiKey: string | null, projectId: string, competitorId?: string): Promise<{
    items: Array<{ date: string; backlinks: number; referring_domains: number; new_backlinks: number | null; lost_backlinks: number | null }>;
  }> => {
    const params = new URLSearchParams({ project_id: projectId });
    if (competitorId) params.set('competitor_id', competitorId);
    return apiCall(`${SEO_SERVICE_URL}/api/backlinks/history?${params}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  triggerBacklinkCheck: async (apiKey: string | null, projectId: string, competitorId?: string): Promise<{ celery_task_id: string }> => {
    const params = new URLSearchParams({ project_id: projectId });
    if (competitorId) params.set('competitor_id', competitorId);
    return apiCall(`${SEO_SERVICE_URL}/api/backlinks/check?${params}`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  // Competitors
  getCompetitors: async (apiKey: string | null, projectId: string): Promise<CompetitorData[]> => {
    return apiCall<CompetitorData[]>(`${SEO_SERVICE_URL}/api/competitors?project_id=${projectId}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  addCompetitor: async (apiKey: string | null, projectId: string, domain: string, source: string = 'seo'): Promise<CompetitorData> => {
    return apiCall<CompetitorData>(`${SEO_SERVICE_URL}/api/competitors`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify({ project_id: projectId, domain, source }),
    });
  },

  discoverCompetitors: async (apiKey: string | null, projectId: string, limit: number = 10): Promise<Array<{
    domain: string;
    avg_position: number | null;
    intersections: number | null;
    full_domain_rank: number | null;
    organic_count: number | null;
  }>> => {
    return apiCall(`${SEO_SERVICE_URL}/api/competitors/discover?project_id=${encodeURIComponent(projectId)}&limit=${limit}`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  suggestCompetitors: async (
    apiKey: string | null,
    domain: string,
    locationCode: number = 2276,
    languageCode: string = 'de',
    limit: number = 10
  ): Promise<Array<{
    domain: string;
    avg_position: number | null;
    intersections: number | null;
    full_domain_rank: number | null;
    organic_count: number | null;
  }>> => {
    const params = new URLSearchParams({
      domain,
      location_code: String(locationCode),
      language_code: languageCode,
      limit: String(limit),
    });
    return apiCall(`${SEO_SERVICE_URL}/api/competitors/suggest?${params}`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  deleteCompetitor: async (apiKey: string | null, competitorId: string): Promise<void> => {
    await apiCall<void>(`${SEO_SERVICE_URL}/api/competitors/${competitorId}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  enrichCompetitor: async (apiKey: string | null, competitorId: string): Promise<CompetitorData> => {
    return apiCall<CompetitorData>(`${SEO_SERVICE_URL}/api/competitors/${competitorId}/enrich`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getKeywordOverlap: async (apiKey: string | null, competitorId: string, limit: number = 50): Promise<Array<{
    keyword: string;
    search_volume: number;
    own_position: number | null;
    competitor_position: number | null;
    cpc: number;
    competition: number;
  }>> => {
    return apiCall(`${SEO_SERVICE_URL}/api/competitors/${competitorId}/keyword-overlap?limit=${limit}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getContentGap: async (apiKey: string | null, competitorId: string, limit: number = 50): Promise<Array<{
    keyword: string;
    search_volume: number;
    competitor_position: number | null;
    cpc: number;
    competition: number;
  }>> => {
    return apiCall(`${SEO_SERVICE_URL}/api/competitors/${competitorId}/content-gap?limit=${limit}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getBacklinkGap: async (apiKey: string | null, competitorId: string, limit: number = 50): Promise<{
    unique_to_competitor: Array<{
      domain: string;
      rank: number | null;
      backlinks: number | null;
      first_seen: string | null;
    }>;
  }> => {
    return apiCall(`${SEO_SERVICE_URL}/api/competitors/${competitorId}/backlink-gap?limit=${limit}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getCompetitorHistory: async (apiKey: string | null, competitorId: string, limit: number = 90): Promise<Array<{
    id: string;
    competitor_id: string;
    domain_authority: number | null;
    organic_traffic_estimate: number | null;
    organic_keywords: number | null;
    total_backlinks: number | null;
    referring_domains: number | null;
    snapshot_date: string;
  }>> => {
    return apiCall(`${SEO_SERVICE_URL}/api/competitors/${competitorId}/history?limit=${limit}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getOwnBacklinkHistory: async (apiKey: string | null, projectId: string, limit: number = 365): Promise<Array<{
    snapshot_date: string;
    total_backlinks: number | null;
    referring_domains: number | null;
    avg_domain_authority: number | null;
    organic_traffic: number | null;
    tracked_keywords: number | null;
    organic_traffic_source: string | null;
  }>> => {
    return apiCall(`${SEO_SERVICE_URL}/api/backlinks/own-history?project_id=${projectId}&limit=${limit}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  // Dashboard
  getDashboard: async (apiKey: string | null): Promise<Record<string, unknown>> => {
    return apiCall<Record<string, unknown>>(`${SEO_SERVICE_URL}/api/dashboard`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  cancelAudit: async (apiKey: string | null, auditId: string): Promise<Audit> => {
    return apiCall<Audit>(`${SEO_SERVICE_URL}/api/audits/${auditId}/cancel`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getSchedules: async (apiKey: string | null, projectId: string): Promise<AuditSchedule[]> => {
    return apiCall<AuditSchedule[]>(`${SEO_SERVICE_URL}/api/schedules?project_id=${projectId}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  createSchedule: async (
    apiKey: string | null,
    data: {
      project_id: string;
      audit_type: string;
      target_domain?: string | null;
      competitor_id?: string | null;
      frequency: string;
      day_of_week?: number | null;
      time_of_day: string;
      crawl_settings?: AuditCrawlSettings | null;
      is_active: boolean;
    }
  ): Promise<AuditSchedule> => {
    return apiCall<AuditSchedule>(`${SEO_SERVICE_URL}/api/schedules`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  updateSchedule: async (
    apiKey: string | null,
    scheduleId: string,
    data: Partial<{
      audit_type: string;
      target_domain: string | null;
      competitor_id: string | null;
      frequency: string;
      day_of_week: number | null;
      time_of_day: string;
      crawl_settings: AuditCrawlSettings | null;
      is_active: boolean;
    }>
  ): Promise<AuditSchedule> => {
    return apiCall<AuditSchedule>(`${SEO_SERVICE_URL}/api/schedules/${scheduleId}`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  deleteSchedule: async (apiKey: string | null, scheduleId: string): Promise<void> => {
    await apiCall<void>(`${SEO_SERVICE_URL}/api/schedules/${scheduleId}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getSchedulesOverview: async (apiKey: string | null, projectId: string): Promise<SchedulesOverview> => {
    return apiCall<SchedulesOverview>(`${SEO_SERVICE_URL}/api/schedules/overview?project_id=${projectId}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  // Pages (audit detail)
  getPages: async (
    apiKey: string | null,
    auditId: string,
    params?: {
      page?: number;
      per_page?: number;
      sort_by?: string;
      sort_dir?: string;
      status_code?: number | null;
      is_indexable?: boolean | null;
      score_min?: number | null;
      score_max?: number | null;
      has_issues?: boolean | null;
      is_https?: boolean | null;
      is_redirect?: boolean | null;
      search?: string;
    }
  ): Promise<PageResult[]> => {
    const q = new URLSearchParams({ audit_id: auditId });
    if (params?.page) q.set('page', String(params.page));
    if (params?.per_page) q.set('per_page', String(params.per_page));
    if (params?.sort_by) q.set('sort_by', params.sort_by);
    if (params?.sort_dir) q.set('sort_dir', params.sort_dir);
    if (params?.status_code != null) q.set('status_code', String(params.status_code));
    if (params?.is_indexable != null) q.set('is_indexable', String(params.is_indexable));
    if (params?.score_min != null) q.set('score_min', String(params.score_min));
    if (params?.score_max != null) q.set('score_max', String(params.score_max));
    if (params?.has_issues != null) q.set('has_issues', String(params.has_issues));
    if (params?.is_https != null) q.set('is_https', String(params.is_https));
    if (params?.is_redirect != null) q.set('is_redirect', String(params.is_redirect));
    if (params?.search) q.set('search', params.search);
    return apiCall<PageResult[]>(`${SEO_SERVICE_URL}/api/pages?${q.toString()}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },


  getIssuesSummary: async (apiKey: string | null, auditId: string): Promise<IssueSummary> => {
    return apiCall<IssueSummary>(`${SEO_SERVICE_URL}/api/pages/issues/summary?audit_id=${auditId}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getPageStats: async (apiKey: string | null, auditId: string): Promise<AuditStats> => {
    return apiCall<AuditStats>(`${SEO_SERVICE_URL}/api/pages/stats?audit_id=${auditId}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getCwvSummary: async (apiKey: string | null, auditId: string): Promise<{
    has_data: boolean;
    total_pages?: number;
    pages_with_cwv?: number;
    avg_lcp_ms?: number | null;
    avg_cls?: number | null;
    avg_fcp_ms?: number | null;
    avg_ttfb_ms?: number | null;
    lcp_good?: number;
    lcp_poor?: number;
    lcp_needs_improvement?: number;
    cls_good?: number;
    cls_poor?: number;
    cls_needs_improvement?: number;
  }> => {
    return apiCall(`${SEO_SERVICE_URL}/api/pages/cwv-summary?audit_id=${encodeURIComponent(auditId)}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  // Filter presets
  getFilterPresets: async (apiKey: string | null): Promise<FilterPreset[]> => {
    return apiCall<FilterPreset[]>(`${SEO_SERVICE_URL}/api/filter-presets`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  createFilterPreset: async (apiKey: string | null, data: { name: string; filter_config: PageFilterConfig }): Promise<FilterPreset> => {
    return apiCall<FilterPreset>(`${SEO_SERVICE_URL}/api/filter-presets`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },


  deleteFilterPreset: async (apiKey: string | null, presetId: string): Promise<void> => {
    await apiCall<void>(`${SEO_SERVICE_URL}/api/filter-presets/${presetId}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getPageCount: async (apiKey: string | null, auditId: string, params?: Record<string, string | number | boolean | null | undefined>): Promise<{ total: number }> => {
    const q = new URLSearchParams({ audit_id: auditId });
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v != null && v !== '') q.set(k, String(v));
      });
    }
    return apiCall<{ total: number }>(`${SEO_SERVICE_URL}/api/pages/count?${q.toString()}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  deleteAudit: async (apiKey: string | null, auditId: string): Promise<void> => {
    await apiCall<void>(`${SEO_SERVICE_URL}/api/audits/${auditId}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  triggerSchedule: async (apiKey: string | null, scheduleId: string): Promise<{ audit_id: string; status: string }> => {
    return apiCall<{ audit_id: string; status: string }>(`${SEO_SERVICE_URL}/api/schedules/${scheduleId}/trigger`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getAuditHistory: async (apiKey: string | null, projectId: string, domain?: string): Promise<AuditHistoryEntry[]> => {
    const params = new URLSearchParams({ project_id: projectId });
    if (domain) params.set('domain', domain);
    return apiCall<AuditHistoryEntry[]>(
      `${SEO_SERVICE_URL}/api/audits/history?${params.toString()}`,
      { headers: { 'X-API-Key': apiKey ?? '' } }
    );
  },

  getGscSummary: async (apiKey: string | null, auditId: string): Promise<GscSummary> => {
    return apiCall<GscSummary>(`${SEO_SERVICE_URL}/api/gsc/summary?audit_id=${auditId}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getGscPages: async (
    apiKey: string | null,
    auditId: string,
    params?: { page?: number; per_page?: number; sort_by?: string; sort_dir?: string; search?: string }
  ): Promise<GscPageMetric[]> => {
    const q = new URLSearchParams({ audit_id: auditId });
    if (params?.page) q.set('page', String(params.page));
    if (params?.per_page) q.set('per_page', String(params.per_page));
    if (params?.sort_by) q.set('sort_by', params.sort_by);
    if (params?.sort_dir) q.set('sort_dir', params.sort_dir);
    if (params?.search) q.set('search', params.search);
    return apiCall<GscPageMetric[]>(`${SEO_SERVICE_URL}/api/gsc/pages?${q.toString()}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getGscKeywords: async (apiKey: string | null, auditId: string, pageUrl?: string, options?: { page?: number; per_page?: number; sort_by?: string; sort_dir?: string; search?: string }): Promise<GscKeywordPaginated> => {
    const params: Record<string, string> = { audit_id: auditId };
    if (pageUrl) params.page_url = pageUrl;
    if (options?.page) params.page = String(options.page);
    if (options?.per_page) params.per_page = String(options.per_page);
    if (options?.sort_by) params.sort_by = options.sort_by;
    if (options?.sort_dir) params.sort_dir = options.sort_dir;
    if (options?.search) params.search = options.search;
    const q = new URLSearchParams(params);
    return apiCall<GscKeywordPaginated>(`${SEO_SERVICE_URL}/api/gsc/keywords?${q.toString()}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  // ── AI Overview ─────────────────────────────────────────────────

  triggerAIOverviewCheck: async (apiKey: string | null, projectId: string, keywordIds?: string[]): Promise<{ task_id: string; status: string }> => {
    return apiCall<{ task_id: string; status: string }>(`${SEO_SERVICE_URL}/api/ai-overview/check`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify({ project_id: projectId, keyword_ids: keywordIds }),
    });
  },

  getAIOverviewResults: async (
    apiKey: string | null,
    projectId: string,
    params?: { audit_id?: string; keyword_text?: string; domain_cited_only?: boolean; page?: number; per_page?: number; sort_by?: string; sort_dir?: string }
  ): Promise<AIOverviewTracking[]> => {
    const q = new URLSearchParams({ project_id: projectId });
    if (params?.audit_id) q.set('audit_id', params.audit_id);
    if (params?.keyword_text) q.set('keyword_text', params.keyword_text);
    if (params?.domain_cited_only) q.set('domain_cited_only', 'true');
    if (params?.page) q.set('page', String(params.page));
    if (params?.per_page) q.set('per_page', String(params.per_page));
    if (params?.sort_by) q.set('sort_by', params.sort_by);
    if (params?.sort_dir) q.set('sort_dir', params.sort_dir);
    return apiCall<AIOverviewTracking[]>(`${SEO_SERVICE_URL}/api/ai-overview/results?${q.toString()}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getAIOverviewSummary: async (apiKey: string | null, projectId: string): Promise<AIOverviewSummary> => {
    return apiCall<AIOverviewSummary>(`${SEO_SERVICE_URL}/api/ai-overview/summary?project_id=${projectId}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  // ── Page Engagement (GA4) ───────────────────────────────────────

  getPageEngagement: async (
    apiKey: string | null,
    auditId: string,
    params?: { page?: number; per_page?: number; sort_by?: string; sort_dir?: string; search?: string }
  ): Promise<PageEngagementData[]> => {
    const q = new URLSearchParams({ audit_id: auditId });
    if (params?.page) q.set('page', String(params.page));
    if (params?.per_page) q.set('per_page', String(params.per_page));
    if (params?.sort_by) q.set('sort_by', params.sort_by);
    if (params?.sort_dir) q.set('sort_dir', params.sort_dir);
    if (params?.search) q.set('search', params.search);
    return apiCall<PageEngagementData[]>(`${SEO_SERVICE_URL}/api/engagement/pages?${q.toString()}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getEngagementSummary: async (apiKey: string | null, auditId: string): Promise<EngagementSummary> => {
    return apiCall<EngagementSummary>(`${SEO_SERVICE_URL}/api/engagement/summary?audit_id=${auditId}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getPageKeywords: async (apiKey: string | null, pageId: string, limit = 50): Promise<PageKeyword[]> => {
    return apiCall<PageKeyword[]>(`${SEO_SERVICE_URL}/api/pages/${pageId}/keywords?limit=${limit}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getPageTypes: async (apiKey: string | null, projectId: string, auditId?: string): Promise<PageTypeStats[]> => {
    const params = new URLSearchParams({ project_id: projectId });
    if (auditId) params.set('audit_id', auditId);
    return apiCall<PageTypeStats[]>(`${SEO_SERVICE_URL}/api/visual-analysis/page-types?${params.toString()}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getSeoDashboard: async (apiKey: string | null, projectId: string, domain?: string): Promise<SeoDashboardResponse> => {
    const params = new URLSearchParams({ project_id: projectId });
    if (domain) params.set('domain', domain);
    return apiCall<SeoDashboardResponse>(`${SEO_SERVICE_URL}/api/dashboard/seo?${params.toString()}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },


  // Admin
  getDataForSeoUsage: async (apiKey: string | null): Promise<DataForSeoUsage> => {
    return apiCall<DataForSeoUsage>(`${SEO_SERVICE_URL}/api/admin/dataforseo-usage`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  // AI Recommendations
  generateRecommendations: async (apiKey: string | null, projectId: string, options?: { include_serp?: boolean; include_backlinks?: boolean }) => {
    return apiCall<{ project_id: string; recommendations: Array<{ priority: number; category: string; title: string; description: string; impact: string; effort: string }>; generated_at: string }>(`${SEO_SERVICE_URL}/api/recommendations/generate`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '', 'Content-Type': 'application/json' },
      body: JSON.stringify({ project_id: projectId, include_serp: options?.include_serp ?? true, include_backlinks: options?.include_backlinks ?? true }),
    });
  },

  analyzeContentQuality: async (apiKey: string | null, content: string, keyword: string) => {
    return apiCall<Record<string, unknown>>(`${SEO_SERVICE_URL}/api/recommendations/content-quality`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '', 'Content-Type': 'application/json' },
      body: JSON.stringify({ content, keyword }),
    });
  },

  getMetaSuggestions: async (apiKey: string | null, url: string, title: string, contentSummary: string, keyword: string) => {
    return apiCall<{ title_suggestions: string[]; description_suggestions: string[] }>(`${SEO_SERVICE_URL}/api/recommendations/meta-suggestions`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '', 'Content-Type': 'application/json' },
      body: JSON.stringify({ url, title, content_summary: contentSummary, keyword }),
    });
  },

  getKeywordCannibalization: async (apiKey: string | null, projectId: string, auditId: string, minPages?: number, page?: number, perPage?: number) => {
    const params = new URLSearchParams({ project_id: projectId, audit_id: auditId });
    if (minPages) params.set('min_pages', String(minPages));
    if (page) params.set('page', String(page));
    if (perPage) params.set('per_page', String(perPage));
    return apiCall<{
      items: Array<{
        keyword: string;
        page_count: number;
        severity: string;
        recommended_page: string;
        pages: Array<{ url: string; page_id: string; wdf_idf: number; in_title: boolean; in_h1: boolean; word_count: number | null }>;
      }>;
      total: number;
      page: number;
      per_page: number;
    }>(`${SEO_SERVICE_URL}/api/keywords/cannibalization?${params.toString()}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  // Issue Tracking
  getTrackedIssues: async (apiKey: string | null, projectId: string, status?: string, page?: number, perPage?: number) => {
    const params = new URLSearchParams({ project_id: projectId });
    if (status) params.append('status', status);
    if (page) params.append('page', String(page));
    if (perPage) params.append('per_page', String(perPage));
    return apiCall<{ items: Array<{ id: string; project_id: string; audit_id: string; issue_type: string; page_url: string; status: string; notes: string | null; resolved_at: string | null; resolved_in_audit_id: string | null; created_at: string; updated_at: string }>; total: number; page: number; per_page: number }>(`${SEO_SERVICE_URL}/api/issues/tracked?${params}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  updateIssueStatus: async (apiKey: string | null, issueId: string, status: string, notes?: string) => {
    return apiCall<{ id: string; status: string; resolved_at: string | null }>(`${SEO_SERVICE_URL}/api/issues/${issueId}/status`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey ?? '', 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, ...(notes ? { notes } : {}) }),
    });
  },

  bulkUpdateIssueStatus: async (apiKey: string | null, issueIds: string[], status: string, notes?: string) => {
    return apiCall<{ updated: number }>(`${SEO_SERVICE_URL}/api/issues/bulk-status`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '', 'Content-Type': 'application/json' },
      body: JSON.stringify({ issue_ids: issueIds, status, ...(notes ? { notes } : {}) }),
    });
  },


  getIssueSummary: async (apiKey: string | null, projectId: string) => {
    return apiCall<{ total: number; new: number; acknowledged: number; in_progress: number; fixed: number; wont_fix: number; reopened: number }>(`${SEO_SERVICE_URL}/api/issues/summary?project_id=${projectId}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  // Page Health Profile
  getPageHealthProfile: async (apiKey: string | null, pageId: string, projectId: string) => {
    return apiCall<{
      crawl_data: Record<string, unknown>;
      backlinks: { count: number; dofollow_count: number; top_sources: Array<{ source_url: string; domain_authority: number | null }>; avg_domain_authority: number };
      keywords: Array<{ keyword: string; current_position: number | null; previous_position: number | null; url: string | null }>;
      gsc_data: { total_clicks: number; total_impressions: number; avg_ctr: number; avg_position: number; top_queries: Array<{ keyword: string; clicks: number; impressions: number; position: number }> };
    }>(`${SEO_SERVICE_URL}/api/pages/${pageId}/health-profile?project_id=${projectId}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  // SEO Alerts
  getAlerts: async (apiKey: string | null, projectId: string, isRead?: boolean, limit?: number) => {
    const params = new URLSearchParams({ project_id: projectId });
    if (isRead !== undefined) params.append('is_read', String(isRead));
    if (limit) params.append('limit', String(limit));
    return apiCall<{ items: Array<{ id: string; project_id: string; alert_type: string; severity: string; title: string; description: string; metadata_json: Record<string, unknown> | null; is_read: boolean; created_at: string }>; total: number; unread_count: number }>(`${SEO_SERVICE_URL}/api/alerts?${params}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  markAlertRead: async (apiKey: string | null, alertId: string) => {
    return apiCall<{ id: string; is_read: boolean }>(`${SEO_SERVICE_URL}/api/alerts/${alertId}/read`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  markAllAlertsRead: async (apiKey: string | null, projectId: string) => {
    return apiCall<{ updated: number }>(`${SEO_SERVICE_URL}/api/alerts/read-all?project_id=${projectId}`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  // ── Phase B: Keyword Maps ─────────────────────────────────────────────────
  keywordMaps: {
    list: (apiKey: string | null, projectId: string) =>
      apiCall<KeywordMapOut[]>(`${SEO_SERVICE_URL}/api/keyword-maps?project_id=${projectId}`, { headers: { 'X-API-Key': apiKey ?? '' } }),
    get: (apiKey: string | null, id: string) =>
      apiCall<KeywordMapOut>(`${SEO_SERVICE_URL}/api/keyword-maps/${id}`, { headers: { 'X-API-Key': apiKey ?? '' } }),
    create: (apiKey: string | null, data: Record<string, unknown>) =>
      apiCall<KeywordMapOut>(`${SEO_SERVICE_URL}/api/keyword-maps`, { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' }, body: JSON.stringify(data) }),
    importGsc: (apiKey: string | null, id: string) =>
      apiCall<KeywordMapImportResult>(`${SEO_SERVICE_URL}/api/keyword-maps/${id}/import-gsc`, { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' } }),
    importCsv: (apiKey: string | null, id: string, keywords: string[]) =>
      apiCall<KeywordMapImportResult>(`${SEO_SERVICE_URL}/api/keyword-maps/${id}/import-csv`, {
        method: 'POST',
        headers: { 'X-API-Key': apiKey ?? '' },
        body: JSON.stringify({ keywords }),
      }),
    runAlgo: (apiKey: string | null, id: string) =>
      apiCall<KeywordMapRunResult>(`${SEO_SERVICE_URL}/api/keyword-maps/${id}/run-algo`, { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' } }),
    runAi: (apiKey: string | null, id: string, scope: string = 'all', entryIds?: string[]) =>
      apiCall<KeywordMapRunResult>(`${SEO_SERVICE_URL}/api/keyword-maps/${id}/run-ai`, {
        method: 'POST',
        headers: { 'X-API-Key': apiKey ?? '', 'Content-Type': 'application/json' },
        body: JSON.stringify({ scope, ...(entryIds ? { entry_ids: entryIds } : {}) }),
      }),
    importUrls: (apiKey: string | null, id: string, urls: string[]) =>
      apiCall<KeywordMapImportResult>(`${SEO_SERVICE_URL}/api/keyword-maps/${id}/import-urls`, {
        method: 'POST',
        headers: { 'X-API-Key': apiKey ?? '', 'Content-Type': 'application/json' },
        body: JSON.stringify({ urls }),
      }),
    addEntry: (apiKey: string | null, mapId: string, url: string, keyword?: string) =>
      apiCall<KeywordMapEntryOut>(`${SEO_SERVICE_URL}/api/keyword-maps/${mapId}/add-entry`, {
        method: 'POST',
        headers: { 'X-API-Key': apiKey ?? '', 'Content-Type': 'application/json' },
        body: JSON.stringify({ url, ...(keyword ? { keyword } : {}) }),
      }),
    deleteEntry: async (apiKey: string | null, entryId: string): Promise<void> => {
      await fetch(`${SEO_SERVICE_URL}/api/keyword-map-entries/${entryId}`, {
        method: 'DELETE',
        headers: { 'X-API-Key': apiKey ?? '' },
      });
    },
    deleteMap: async (apiKey: string | null, mapId: string): Promise<void> => {
      await fetch(`${SEO_SERVICE_URL}/api/keyword-maps/${mapId}`, {
        method: 'DELETE',
        headers: { 'X-API-Key': apiKey ?? '' },
      });
    },
    entries: (apiKey: string | null, mapId: string, opts: { all?: boolean; page?: number; perPage?: number } = {}) => {
      const { all = false, page = 1, perPage = 200 } = opts;
      const query = all ? 'all=true' : `page=${page}&per_page=${perPage}`;
      return apiCall<KeywordMapEntryListResponse>(`${SEO_SERVICE_URL}/api/keyword-maps/${mapId}/entries?${query}`, { headers: { 'X-API-Key': apiKey ?? '' } });
    },
    patchEntry: (apiKey: string | null, entryId: string, data: Record<string, unknown>) =>
      apiCall<KeywordMapEntryOut>(`${SEO_SERVICE_URL}/api/keyword-map-entries/${entryId}`, { method: 'PATCH', headers: { 'X-API-Key': apiKey ?? '' }, body: JSON.stringify(data) }),
    acceptAiField: (apiKey: string | null, entryId: string, fields: string[]) =>
      apiCall<KeywordMapEntryOut>(`${SEO_SERVICE_URL}/api/keyword-map-entries/${entryId}/accept-ai-field`, {
        method: 'PATCH',
        headers: { 'X-API-Key': apiKey ?? '', 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields }),
      }),
    rejectAi: (apiKey: string | null, entryId: string) =>
      apiCall<KeywordMapEntryOut>(`${SEO_SERVICE_URL}/api/keyword-map-entries/${entryId}/reject-ai`, {
        method: 'PATCH',
        headers: { 'X-API-Key': apiKey ?? '' },
      }),
    enrich: (apiKey: string | null, mapId: string, data: { fields?: string[]; scope?: string; entry_ids?: string[]; consented?: boolean; consent_auto?: boolean }) =>
      apiCall<{ enriched: number; skipped: number; cost_usd: number; fields: string[]; estimated?: boolean; keywords?: string[]; message?: string }>(`${SEO_SERVICE_URL}/api/keyword-maps/${mapId}/enrich`, {
        method: 'POST',
        headers: { 'X-API-Key': apiKey ?? '', 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }),
    bulkAcceptAi: (apiKey: string | null, mapId: string, fields?: string[]) =>
      apiCall<{ accepted: number; map_id: string; fields: string[]; cannibalization_warnings?: Array<{ keyword: string; slot: string; other_url: string; other_entry_id: string; recommended_page: string; entry_id?: string }> }>(`${SEO_SERVICE_URL}/api/keyword-maps/${mapId}/bulk-accept-ai`, {
        method: 'POST',
        headers: { 'X-API-Key': apiKey ?? '', 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields: fields ?? ['keyword', 'secondary_keyword', 'tertiary_keyword'] }),
      }),
    rejectAllAi: (apiKey: string | null, mapId: string) =>
      apiCall<{ cleared: number; map_id: string }>(`${SEO_SERVICE_URL}/api/keyword-maps/${mapId}/reject-all-ai`, {
        method: 'POST',
        headers: { 'X-API-Key': apiKey ?? '' },
      }),
    fromTracked: (apiKey: string | null, data: { project_id: string; name?: string }) =>
      apiCall<KeywordMapOut>(`${SEO_SERVICE_URL}/api/keyword-maps/from-tracked`, {
        method: 'POST',
        headers: { 'X-API-Key': apiKey ?? '', 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }),
    addKeywords: (apiKey: string | null, mapId: string, keywords: string[]) =>
      apiCall<{ added: number; map_id: string }>(`${SEO_SERVICE_URL}/api/keyword-maps/${mapId}/add-keywords`, {
        method: 'POST',
        headers: { 'X-API-Key': apiKey ?? '', 'Content-Type': 'application/json' },
        body: JSON.stringify({ keywords }),
      }),
    syncToTracking: (apiKey: string | null, mapId: string) =>
      apiCall<{ synced: number; skipped: number; map_id: string }>(`${SEO_SERVICE_URL}/api/keyword-maps/${mapId}/sync-to-tracking`, {
        method: 'POST',
        headers: { 'X-API-Key': apiKey ?? '' },
      }),
    syncCrawlUrls: (apiKey: string | null, mapId: string) =>
      apiCall<{ added: number; updated: number; deduped?: number; total_pages: number; audit_id: string | null; map_id: string }>(`${SEO_SERVICE_URL}/api/keyword-maps/${mapId}/sync-crawl-urls`, {
        method: 'POST',
        headers: { 'X-API-Key': apiKey ?? '' },
      }),
    urlAggregate: (apiKey: string | null, mapId: string) =>
      apiCall<{ map_id: string; audit_id: string | null; urls: Array<{ url: string; total_volume: number; keyword_count: number; gsc_clicks: number; gsc_impressions: number; gsc_position: number | null }>; crawled_urls: string[] }>(`${SEO_SERVICE_URL}/api/keyword-maps/${mapId}/urls/aggregate`, {
        headers: { 'X-API-Key': apiKey ?? '' },
      }),
    cannibalization: (apiKey: string | null, mapId: string) =>
      apiCall<{ map_id: string; conflicts: Array<{ keyword: string; urls: string[]; entry_ids: string[]; slots: Array<{ entry_id: string; slot: string; url: string }>; recommended_page: string }>; conflict_count: number }>(`${SEO_SERVICE_URL}/api/keyword-maps/${mapId}/cannibalization`, {
        headers: { 'X-API-Key': apiKey ?? '' },
      }),
    refocusEntry: (apiKey: string | null, entryId: string, data: { consented?: boolean; consent_auto?: boolean }) =>
      apiCall<{ entry_id: string; consented: boolean; consent_auto?: boolean; cost_usd: number; keywords?: string[]; message?: string; slots_updated?: number; tracked_updated?: boolean; entry?: KeywordMapEntryOut }>(`${SEO_SERVICE_URL}/api/keyword-map-entries/${entryId}/refocus`, {
        method: 'POST',
        headers: { 'X-API-Key': apiKey ?? '', 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }),
    snapshot: (apiKey: string | null, mapId: string, name?: string) =>
      apiCall<{ id: string; map_id: string; name: string | null; kind: string; entry_count: number; created_at: string | null }>(`${SEO_SERVICE_URL}/api/keyword-maps/${mapId}/snapshot${name ? `?name=${encodeURIComponent(name)}` : ''}`, {
        method: 'POST',
        headers: { 'X-API-Key': apiKey ?? '' },
      }),
    snapshots: (apiKey: string | null, mapId: string) =>
      apiCall<{ map_id: string; snapshots: Array<{ id: string; name: string | null; kind: string; created_by: string | null; entry_count: number; created_at: string | null }> }>(`${SEO_SERVICE_URL}/api/keyword-maps/${mapId}/snapshots`, {
        headers: { 'X-API-Key': apiKey ?? '' },
      }),
    compareSnapshots: (apiKey: string | null, mapId: string, snapshotAId: string, snapshotBId: string) =>
      apiCall<{ snapshot_a: string; snapshot_b: string; added_urls: string[]; removed_urls: string[]; changed_urls: Array<{ url: string; before: Record<string, unknown>; after: Record<string, unknown> }>; counts: { a: number; b: number; added: number; removed: number; changed: number } }>(`${SEO_SERVICE_URL}/api/keyword-maps/${mapId}/compare`, {
        method: 'POST',
        headers: { 'X-API-Key': apiKey ?? '', 'Content-Type': 'application/json' },
        body: JSON.stringify({ snapshot_a_id: snapshotAId, snapshot_b_id: snapshotBId }),
      }),
  },


  // ── Phase B: Content Optimization ────────────────────────────────────────
  contentOptimization: {
    getPageTree: (apiKey: string | null, auditId: string) =>
      apiCall<PageTreeResponse>(`${SEO_SERVICE_URL}/api/audits/${auditId}/page-tree`, { headers: { 'X-API-Key': apiKey ?? '' } }),
  },

  // ── Phase C: Migrations ───────────────────────────────────────────────────
  migrations: {
    list: (apiKey: string | null, projectId: string) =>
      apiCall<unknown[]>(`${SEO_SERVICE_URL}/api/migrations?project_id=${projectId}`, { headers: { 'X-API-Key': apiKey ?? '' } }),
    get: (apiKey: string | null, id: string) =>
      apiCall<unknown>(`${SEO_SERVICE_URL}/api/migrations/${id}`, { headers: { 'X-API-Key': apiKey ?? '' } }),
    create: (apiKey: string | null, data: Record<string, unknown>) =>
      apiCall<unknown>(`${SEO_SERVICE_URL}/api/migrations`, { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' }, body: JSON.stringify(data) }),
    delete: (apiKey: string | null, id: string) =>
      apiCall<void>(`${SEO_SERVICE_URL}/api/migrations/${id}`, { method: 'DELETE', headers: { 'X-API-Key': apiKey ?? '' } }),
    runMatch: (apiKey: string | null, id: string, algorithm = 'algo') =>
      apiCall<unknown>(`${SEO_SERVICE_URL}/api/migrations/${id}/run-match`, { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' }, body: JSON.stringify({ algorithm }) }),
    runCompare: (apiKey: string | null, id: string) =>
      apiCall<unknown>(`${SEO_SERVICE_URL}/api/migrations/${id}/run-compare`, { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' } }),
    // Pairs
    listPairs: (apiKey: string | null, id: string, page = 1, perPage = 50, params: Record<string, string | boolean> = {}) => {
      const qs = new URLSearchParams({ page: String(page), per_page: String(perPage) });
      Object.entries(params).forEach(([k, v]) => qs.set(k, String(v)));
      return apiCall<unknown[]>(`${SEO_SERVICE_URL}/api/migrations/${id}/pairs?${qs}`, { headers: { 'X-API-Key': apiKey ?? '' } });
    },
    countPairs: (apiKey: string | null, id: string, params: Record<string, string | boolean> = {}) => {
      const qs = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => qs.set(k, String(v)));
      return apiCall<unknown>(`${SEO_SERVICE_URL}/api/migrations/${id}/pairs/count${qs.size ? `?${qs}` : ''}`, { headers: { 'X-API-Key': apiKey ?? '' } });
    },
    updatePair: (apiKey: string | null, pairId: string, data: Record<string, unknown>) =>
      apiCall<unknown>(`${SEO_SERVICE_URL}/api/migration-pairs/${pairId}`, { method: 'PATCH', headers: { 'X-API-Key': apiKey ?? '' }, body: JSON.stringify(data) }),
    deletePair: (apiKey: string | null, pairId: string) =>
      apiCall<void>(`${SEO_SERVICE_URL}/api/migration-pairs/${pairId}`, { method: 'DELETE', headers: { 'X-API-Key': apiKey ?? '' } }),
    // CSV
    uploadSourceCsv: (apiKey: string | null, id: string, file: File) => {
      const fd = new FormData(); fd.append('file', file);
      return apiCall<unknown>(`${SEO_SERVICE_URL}/api/migrations/${id}/upload-source-csv`, { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' }, body: fd });
    },
    uploadTargetCsv: (apiKey: string | null, id: string, file: File) => {
      const fd = new FormData(); fd.append('file', file);
      return apiCall<unknown>(`${SEO_SERVICE_URL}/api/migrations/${id}/upload-target-csv`, { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' }, body: fd });
    },
    uploadMappingCsv: (apiKey: string | null, id: string, file: File) => {
      const fd = new FormData(); fd.append('file', file);
      return apiCall<unknown>(`${SEO_SERVICE_URL}/api/migrations/${id}/upload-mapping-csv`, { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' }, body: fd });
    },
    listDiffs: (apiKey: string | null, id: string, comparedOnly = false) =>
      apiCall<unknown[]>(`${SEO_SERVICE_URL}/api/migrations/${id}/diffs${comparedOnly ? '?compared_only=true' : ''}`, { headers: { 'X-API-Key': apiKey ?? '' } }),
    // Target URL pool & audit linking
    getTargetUrls: (apiKey: string | null, id: string) =>
      apiCall<{ url: string; label: string | null }[]>(`${SEO_SERVICE_URL}/api/migrations/${id}/target-urls`, { headers: { 'X-API-Key': apiKey ?? '' } }),
    linkTargetAudit: (apiKey: string | null, id: string, auditId: string) =>
      apiCall<unknown>(`${SEO_SERVICE_URL}/api/migrations/${id}/link-target-audit`, { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' }, body: JSON.stringify({ audit_id: auditId }) }),
    linkSourceAudit: (apiKey: string | null, id: string, auditId: string) =>
      apiCall<unknown>(`${SEO_SERVICE_URL}/api/migrations/${id}/link-source-audit`, { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' }, body: JSON.stringify({ audit_id: auditId }) }),
    // Lock management
    lockHighConfidence: (apiKey: string | null, id: string, threshold: number) =>
      apiCall<unknown>(`${SEO_SERVICE_URL}/api/migrations/${id}/lock-high-confidence`, { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' }, body: JSON.stringify({ threshold }) }),
    unlockAll: (apiKey: string | null, id: string) =>
      apiCall<unknown>(`${SEO_SERVICE_URL}/api/migrations/${id}/unlock-all`, { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' } }),
    // .htaccess
    getHtaccess: (apiKey: string | null, id: string) =>
      fetch(`${SEO_SERVICE_URL}/api/migrations/${id}/htaccess`, { headers: { 'X-API-Key': apiKey ?? '' } }).then(r => r.text()),
  },


  // ── Phase C: GEO ─────────────────────────────────────────────────────────
  geo: {
    overview: (apiKey: string | null, projectId: string) =>
      apiCall<unknown>(`${SEO_SERVICE_URL}/api/geo/overview?project_id=${projectId}`, { headers: { 'X-API-Key': apiKey ?? '' } }),
    mentions: (apiKey: string | null, projectId: string, keyword?: string, platform?: string) => {
      const params = new URLSearchParams({ project_id: projectId });
      if (keyword) params.set('keyword', keyword);
      if (platform) params.set('platform', platform);
      return apiCall<unknown[]>(`${SEO_SERVICE_URL}/api/geo/mentions?${params}`, { headers: { 'X-API-Key': apiKey ?? '' } });
    },
    refreshMentions: (apiKey: string | null, projectId: string) =>
      apiCall<unknown>(`${SEO_SERVICE_URL}/api/geo/mentions/refresh?project_id=${projectId}`, { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' } }),
    faqAudit: (apiKey: string | null, projectId: string) =>
      apiCall<Array<{ page_id: string | null; url: string; title: string | null; has_faq_schema: boolean; schema_valid: boolean; faq_count: number | null; geo_aeo_score: number | null }>>(`${SEO_SERVICE_URL}/api/geo/faq-audit?project_id=${projectId}`, { headers: { 'X-API-Key': apiKey ?? '' } }),
    domainVisibility: (apiKey: string | null, projectId: string) =>
      apiCall<Array<{ domain: string; competitor_id: string | null; is_master: boolean; platforms: Array<{ platform: string; mentions_count: number | null; ai_search_volume: number | null; top_sources: string[] | null; fetched_at: string | null }> }>>(`${SEO_SERVICE_URL}/api/geo/domain-visibility?project_id=${projectId}`, { headers: { 'X-API-Key': apiKey ?? '' } }),
    refreshDomainVisibility: (apiKey: string | null, projectId: string) =>
      apiCall<unknown>(`${SEO_SERVICE_URL}/api/geo/domain-visibility/refresh?project_id=${projectId}`, { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' } }),
    history: (apiKey: string | null, projectId: string, keyword?: string, days?: number) => {
      const params = new URLSearchParams({ project_id: projectId });
      if (keyword) params.set('keyword', keyword);
      if (days) params.set('days', String(days));
      return apiCall<Array<{ snapshot_date: string; mentions_count: number | null; ai_search_volume: number | null }>>(
        `${SEO_SERVICE_URL}/api/geo/history?${params}`, { headers: { 'X-API-Key': apiKey ?? '' } });
    },
  },

  // ── Phase D: Visual Analysis ──────────────────────────────────────────────
  visualAnalysis: {
    pageTypes: (apiKey: string | null, projectId: string, auditId?: string) => {
      const params = new URLSearchParams({ project_id: projectId });
      if (auditId) params.set('audit_id', auditId);
      return apiCall<unknown[]>(`${SEO_SERVICE_URL}/api/visual-analysis/page-types?${params}`, { headers: { 'X-API-Key': apiKey ?? '' } });
    },
    analyze: (apiKey: string | null, data: Record<string, unknown>) =>
      apiCall<unknown>(`${SEO_SERVICE_URL}/api/visual-analysis/analyze`, { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' }, body: JSON.stringify(data) }),
    list: (apiKey: string | null, projectId: string) =>
      apiCall<unknown[]>(`${SEO_SERVICE_URL}/api/visual-analysis?project_id=${projectId}`, { headers: { 'X-API-Key': apiKey ?? '' } }),
  },

  // ── Phase D: A/B Hypotheses ───────────────────────────────────────────────
  abHypotheses: {
    list: (apiKey: string | null, projectId: string) =>
      apiCall<unknown[]>(`${SEO_SERVICE_URL}/api/ab-hypotheses?project_id=${projectId}`, { headers: { 'X-API-Key': apiKey ?? '' } }),
    start: (apiKey: string | null, hypothesisId: string) =>
      apiCall<unknown>(`${SEO_SERVICE_URL}/api/ab-hypotheses/${hypothesisId}/start`, { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' } }),
    conclude: (apiKey: string | null, hypothesisId: string) =>
      apiCall<unknown>(`${SEO_SERVICE_URL}/api/ab-hypotheses/${hypothesisId}/conclude`, { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' } }),
    results: (apiKey: string | null, hypothesisId: string) =>
      apiCall<unknown>(`${SEO_SERVICE_URL}/api/ab-hypotheses/${hypothesisId}/results`, { headers: { 'X-API-Key': apiKey ?? '' } }),
  },

  // ── Phase D: Irys SEO Index ───────────────────────────────────────────────
  irysIndex: {
    summary: (apiKey: string | null, auditId: string) =>
      apiCall<unknown>(`${SEO_SERVICE_URL}/api/audits/${auditId}/irys-index/summary`, { headers: { 'X-API-Key': apiKey ?? '' } }),
    regenerate: (apiKey: string | null, auditId: string) =>
      apiCall<unknown>(`${SEO_SERVICE_URL}/api/audits/${auditId}/irys-index/regenerate`, { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' } }),
    history: (apiKey: string | null, projectId: string) =>
      apiCall<unknown[]>(`${SEO_SERVICE_URL}/api/projects/${projectId}/irys-index/history`, { headers: { 'X-API-Key': apiKey ?? '' } }),
    updateWeights: (apiKey: string | null, projectId: string, weights: Record<string, number>) =>
      apiCall<unknown>(`${SEO_SERVICE_URL}/api/projects/${projectId}/irys-weights`, { method: 'PATCH', headers: { 'X-API-Key': apiKey ?? '' }, body: JSON.stringify(weights) }),
  },

  // ── Phase A+D: Spending ───────────────────────────────────────────────────
  spending: {
    summary: (apiKey: string | null, projectId?: string) => {
      const params = new URLSearchParams();
      if (projectId) params.set('project_id', projectId);
      return apiCall<unknown>(`${SEO_SERVICE_URL}/api/spending/summary${params.size ? `?${params}` : ''}`, { headers: { 'X-API-Key': apiKey ?? '' } });
    },
  },

  // ── Phase A+D: SEO Tasks ──────────────────────────────────────────────────
  seoTasks: {
    list: (apiKey: string | null, projectId: string, options: SeoTaskListOptions = {}): Promise<SeoTaskListResponse> => {
      const params = new URLSearchParams({ project_id: projectId });
      if (options.status) params.set('status', options.status);
      if (options.source) params.set('source', options.source);
      if (options.severity) params.set('severity', options.severity);
      if (options.category) params.set('category', options.category);
      if (options.page) params.set('page', String(options.page));
      if (options.perPage) params.set('per_page', String(options.perPage));

      return apiCall<SeoTaskListResponse>(`${SEO_SERVICE_URL}/api/seo-tasks?${params}`, {
        headers: { 'X-API-Key': apiKey ?? '' },
      });
    },
    generateAi: (apiKey: string | null, projectId: string, maxTasks?: number, categories?: string[]) => {
      const params = new URLSearchParams({ project_id: projectId });
      if (maxTasks) params.set('max_tasks', String(maxTasks));
      if (categories && categories.length > 0) params.set('categories', categories.join(','));
      return apiCall<unknown>(`${SEO_SERVICE_URL}/api/seo-tasks/generate-ai?${params}`, { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' } });
    },
    generateGeo: (apiKey: string | null, projectId: string) =>
      apiCall<{ created: number; source: string }>(`${SEO_SERVICE_URL}/api/seo-tasks/generate-geo?project_id=${projectId}`, { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' } }),
    gamification: (apiKey: string | null, projectId: string) =>
      apiCall<SeoGamificationState>(`${SEO_SERVICE_URL}/api/seo-tasks/gamification?project_id=${projectId}`, { headers: { 'X-API-Key': apiKey ?? '' } }),
    action: (apiKey: string | null, taskId: string, action: string, scheduledFor?: string) =>
      apiCall<unknown>(`${SEO_SERVICE_URL}/api/seo-tasks/${taskId}/action`, { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' }, body: JSON.stringify({ action, scheduled_for: scheduledFor ?? null }) }),
  },

  // ── Phase 3: Content Optimization Workbench ────────────────────────────────
  optimization: {
    getCandidates: (apiKey: string | null, projectId: string, params?: { tier?: number; min_opportunity_score?: number; limit?: number }) => {
      const qs = new URLSearchParams();
      if (params?.tier !== undefined) qs.set('tier', String(params.tier));
      if (params?.min_opportunity_score !== undefined) qs.set('min_opportunity_score', String(params.min_opportunity_score));
      if (params?.limit !== undefined) qs.set('limit', String(params.limit));
      return apiCall<OptimizationCandidate[]>(`${SEO_SERVICE_URL}/api/projects/${projectId}/optimization-candidates${qs.size ? `?${qs}` : ''}`, { headers: { 'X-API-Key': apiKey ?? '' } });
    },
    getPresets: (apiKey: string | null, projectId: string) =>
      apiCall<OptimizationPreset[]>(`${SEO_SERVICE_URL}/api/projects/${projectId}/optimization-presets`, { headers: { 'X-API-Key': apiKey ?? '' } }),
    createPreset: (apiKey: string | null, projectId: string, data: Partial<OptimizationPreset>) =>
      apiCall<OptimizationPreset>(`${SEO_SERVICE_URL}/api/projects/${projectId}/optimization-presets`, { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' }, body: JSON.stringify(data) }),
    updatePreset: (apiKey: string | null, projectId: string, presetId: string, data: Partial<OptimizationPreset>) =>
      apiCall<OptimizationPreset>(`${SEO_SERVICE_URL}/api/projects/${projectId}/optimization-presets/${presetId}`, { method: 'PATCH', headers: { 'X-API-Key': apiKey ?? '' }, body: JSON.stringify(data) }),
    deletePreset: (apiKey: string | null, projectId: string, presetId: string) =>
      apiCall<void>(`${SEO_SERVICE_URL}/api/projects/${projectId}/optimization-presets/${presetId}`, { method: 'DELETE', headers: { 'X-API-Key': apiKey ?? '' } }),
    getOptimizationProjects: (apiKey: string | null, projectId: string) =>
      apiCall<OptimizationProject[]>(`${SEO_SERVICE_URL}/api/projects/${projectId}/optimization-projects`, { headers: { 'X-API-Key': apiKey ?? '' } }),
    getOptimizationProject: (apiKey: string | null, projectId: string, opId: string) =>
      apiCall<OptimizationProject>(`${SEO_SERVICE_URL}/api/projects/${projectId}/optimization-projects/${opId}`, { headers: { 'X-API-Key': apiKey ?? '' } }),
    createOptimizationProject: (apiKey: string | null, projectId: string, data: Partial<OptimizationProject>) =>
      apiCall<OptimizationProject>(`${SEO_SERVICE_URL}/api/projects/${projectId}/optimization-projects`, { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' }, body: JSON.stringify(data) }),
    updateOptimizationProject: (apiKey: string | null, projectId: string, opId: string, data: Partial<OptimizationProject>) =>
      apiCall<OptimizationProject>(`${SEO_SERVICE_URL}/api/projects/${projectId}/optimization-projects/${opId}`, { method: 'PATCH', headers: { 'X-API-Key': apiKey ?? '' }, body: JSON.stringify(data) }),
    generateBrief: (apiKey: string | null, projectId: string, opId: string, structure?: Array<{ stype: string; label: string; words: number }>, briefSettings?: { tonality?: string; instructions?: string }) =>
      apiCall<{ task_id: string; status: string }>(`${SEO_SERVICE_URL}/api/projects/${projectId}/optimization-projects/${opId}/generate-brief`, { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' }, body: JSON.stringify({ structure: structure ?? null, brief_settings: briefSettings ?? null }) }),
    getBriefStatus: (apiKey: string | null, projectId: string, opId: string) =>
      apiCall<{ status: string; brief_generated_at: string | null; has_brief: boolean }>(`${SEO_SERVICE_URL}/api/projects/${projectId}/optimization-projects/${opId}/brief-status`, { headers: { 'X-API-Key': apiKey ?? '' } }),
    generateFullPage: (apiKey: string | null, projectId: string, opId: string, existingDrafts?: Record<string, string>) =>
      apiCall<{ task_id: string; status: string }>(`${SEO_SERVICE_URL}/api/projects/${projectId}/optimization-projects/${opId}/generate-full-page`, { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' }, body: JSON.stringify({ existing_drafts: existingDrafts ?? {} }) }),
    finalize: (apiKey: string | null, projectId: string, opId: string) =>
      apiCall<{ task_id: string; status: string }>(`${SEO_SERVICE_URL}/api/projects/${projectId}/optimization-projects/${opId}/finalize`, { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' } }),
    runPipeline: (apiKey: string | null, projectId: string, opId: string, maxIterations?: number) =>
      apiCall<{ task_id: string; status: string }>(`${SEO_SERVICE_URL}/api/projects/${projectId}/optimization-projects/${opId}/run-pipeline`, { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' }, body: JSON.stringify({ max_iterations: maxIterations ?? null }) }),
    runIteration: (apiKey: string | null, projectId: string, opId: string) =>
      apiCall<{ task_id: string; status: string }>(`${SEO_SERVICE_URL}/api/projects/${projectId}/optimization-projects/${opId}/iterate`, { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' } }),
    getIterations: (apiKey: string | null, projectId: string, opId: string) =>
      apiCall<OptimizationIteration[]>(`${SEO_SERVICE_URL}/api/projects/${projectId}/optimization-projects/${opId}/iterations`, { headers: { 'X-API-Key': apiKey ?? '' } }),
    recontext: (apiKey: string | null, projectId: string, opId: string) =>
      apiCall<{ task_id: string; status: string; next: string }>(`${SEO_SERVICE_URL}/api/projects/${projectId}/optimization-projects/${opId}/recontext`, { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' } }),
    scoreContent: (apiKey: string | null, projectId: string, opId: string, data: { page_data: Record<string, unknown>; content_html?: string }) =>
      apiCall<ContentScoreResult>(`${SEO_SERVICE_URL}/api/projects/${projectId}/optimization-projects/${opId}/score`, { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' }, body: JSON.stringify(data) }),
    serpAnalysis: (apiKey: string | null, projectId: string, opId: string) =>
      apiCall<SerpAnalysisResult>(`${SEO_SERVICE_URL}/api/projects/${projectId}/optimization-projects/${opId}/serp-analysis`, { headers: { 'X-API-Key': apiKey ?? '' } }),
    keywordMetrics: (apiKey: string | null, projectId: string, opId: string, keyword?: string) =>
      apiCall<{ keyword: string; metrics: { search_volume: number | null; difficulty: number | null; intent: string | null; cpc: number | null } | null; error: string | null; consent_required?: boolean }>(`${SEO_SERVICE_URL}/api/projects/${projectId}/optimization-projects/${opId}/keyword-metrics${keyword ? `?keyword=${encodeURIComponent(keyword)}` : ''}`, { headers: { 'X-API-Key': apiKey ?? '' } }),
    reportUrl: (projectId: string, opId: string, format: 'report' | 'content' | 'report-pdf' | 'content-pdf' = 'report') =>
      `${SEO_SERVICE_URL}/api/projects/${projectId}/optimization-projects/${opId}/report?format=${format}`,
    getUrlKeywords: (apiKey: string | null, projectId: string, url: string) =>
      apiCall<Array<{ keyword: string; source: string; impressions: number | null; clicks: number | null; position: number | null; search_volume: number | null; priority?: number }>>(`${SEO_SERVICE_URL}/api/projects/${projectId}/url-keywords?url=${encodeURIComponent(url)}`, { headers: { 'X-API-Key': apiKey ?? '' } }),
    getKeywordSuggestions: (apiKey: string | null, projectId: string, opId: string, seed: string, limit = 15) =>
      apiCall<Array<{ keyword: string; search_volume: number | null; difficulty: number | null; intent: string | null; cpc: number | null; source: string }>>(`${SEO_SERVICE_URL}/api/projects/${projectId}/optimization-projects/${opId}/keyword-suggestions?seed=${encodeURIComponent(seed)}&limit=${limit}`, { headers: { 'X-API-Key': apiKey ?? '' } }),
    analyzeStructure: (apiKey: string | null, projectId: string, opId: string) =>
      apiCall<{ has_crawled: boolean; source: string; page_url: string | null; current_structure: Array<{ tag: string | null; text: string | null }>; ai_structure: Array<{ stype: string | null; label: string | null; words: number | null }>; template_name: string | null; ai_error: string | null; word_count: number | null }>(`${SEO_SERVICE_URL}/api/projects/${projectId}/optimization-projects/${opId}/analyze-structure`, { headers: { 'X-API-Key': apiKey ?? '' }, timeoutMs: 120_000 }),
    getSteps: (apiKey: string | null, projectId: string, opId: string) =>
      apiCall<GenerationStep[]>(`${SEO_SERVICE_URL}/api/projects/${projectId}/optimization-projects/${opId}/steps`, { headers: { 'X-API-Key': apiKey ?? '' } }),
    updateStep: (apiKey: string | null, projectId: string, opId: string, stepId: string, data: Partial<GenerationStep>) =>
      apiCall<GenerationStep>(`${SEO_SERVICE_URL}/api/projects/${projectId}/optimization-projects/${opId}/steps/${stepId}`, { method: 'PATCH', headers: { 'X-API-Key': apiKey ?? '' }, body: JSON.stringify(data) }),
    verifyClaim: (apiKey: string | null, projectId: string, opId: string, data: { claim: string }) =>
      apiCall<{ sources: Array<{ url: string; title: string; snippet: string }> }>(`${SEO_SERVICE_URL}/api/projects/${projectId}/optimization-projects/${opId}/verify-claim`, { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' }, body: JSON.stringify(data) }),
    suggestKeywords: (apiKey: string | null, projectId: string, data: { url: string; page_title?: string; page_content_snippet?: string; language?: string }) =>
      apiCall<{ focus_keyword: string; secondary_keywords: string[]; tokens_used: number }>(`${SEO_SERVICE_URL}/api/ai/suggest/keywords`, { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' }, body: JSON.stringify({ project_id: projectId, ...data }) }),
  },

  crawlData: {
    getLinks: (apiKey: string | null, auditId: string, linkType: 'all' | 'internal' | 'external' = 'all', page = 1, perPage = 50) => {
      const params = new URLSearchParams({ link_type: linkType, page: String(page), per_page: String(perPage) });
      return apiCall<CrawledLink[]>(`${SEO_SERVICE_URL}/api/crawl-data/${auditId}/links?${params}`, { headers: { 'X-API-Key': apiKey ?? '' } });
    },
    getHreflang: (apiKey: string | null, auditId: string) =>
      apiCall<HreflangTag[]>(`${SEO_SERVICE_URL}/api/crawl-data/${auditId}/hreflang`, { headers: { 'X-API-Key': apiKey ?? '' } }),
    getSchemaMarkup: (apiKey: string | null, auditId: string) =>
      apiCall<SchemaMarkup[]>(`${SEO_SERVICE_URL}/api/crawl-data/${auditId}/schema-markup`, { headers: { 'X-API-Key': apiKey ?? '' } }),
    getDuplicates: (apiKey: string | null, auditId: string) =>
      apiCall<DuplicateContent[]>(`${SEO_SERVICE_URL}/api/crawl-data/${auditId}/duplicates`, { headers: { 'X-API-Key': apiKey ?? '' } }),
    getRedirects: (apiKey: string | null, auditId: string) =>
      apiCall<CrawlRedirect[]>(`${SEO_SERVICE_URL}/api/crawl-data/${auditId}/redirects`, { headers: { 'X-API-Key': apiKey ?? '' } }),
    getImages: (apiKey: string | null, auditId: string, missingAltOnly = false, page = 1, perPage = 50) => {
      const params = new URLSearchParams({ missing_alt_only: String(missingAltOnly), page: String(page), per_page: String(perPage) });
      return apiCall<ImageInfo[]>(`${SEO_SERVICE_URL}/api/crawl-data/${auditId}/images?${params}`, { headers: { 'X-API-Key': apiKey ?? '' } });
    },
    getResources: (apiKey: string | null, auditId: string, resourceType?: string, renderBlockingOnly = false, page = 1, perPage = 50) => {
      const params = new URLSearchParams({ render_blocking_only: String(renderBlockingOnly), page: String(page), per_page: String(perPage) });
      if (resourceType) params.set('resource_type', resourceType);
      return apiCall<ResourceAsset[]>(`${SEO_SERVICE_URL}/api/crawl-data/${auditId}/resources?${params}`, { headers: { 'X-API-Key': apiKey ?? '' } });
    },
    getCrawlStats: (apiKey: string | null, auditId: string) =>
      apiCall<CrawlStats>(`${SEO_SERVICE_URL}/api/crawl-data/${auditId}/crawl-stats`, { headers: { 'X-API-Key': apiKey ?? '' } }),

    // ── New crawl features ──────────────────────────────────────────────

    compareCrawls: (apiKey: string | null, auditId: string, compareWith: string) =>
      apiCall<CrawlComparisonResponse>(`${SEO_SERVICE_URL}/api/crawl-data/${auditId}/compare?compare_with=${compareWith}`, { headers: { 'X-API-Key': apiKey ?? '' } }),

    getCWV: (apiKey: string | null, auditId: string) =>
      apiCall<CWVSummary>(`${SEO_SERVICE_URL}/api/crawl-data/${auditId}/cwv`, { headers: { 'X-API-Key': apiKey ?? '' } }),

    getRobotsTxt: (apiKey: string | null, auditId: string) =>
      apiCall<RobotsTesterResult>(`${SEO_SERVICE_URL}/api/crawl-data/${auditId}/robots-txt`, { headers: { 'X-API-Key': apiKey ?? '' } }),

    getSitemapAnalysis: (apiKey: string | null, auditId: string) =>
      apiCall<SitemapAnalysisResult>(`${SEO_SERVICE_URL}/api/crawl-data/${auditId}/sitemap-analysis`, { headers: { 'X-API-Key': apiKey ?? '' } }),

    getMobileCheck: (apiKey: string | null, auditId: string, perPage = 50) =>
      apiCall<MobileFriendlySummary>(`${SEO_SERVICE_URL}/api/crawl-data/${auditId}/mobile-check?per_page=${perPage}`, { headers: { 'X-API-Key': apiKey ?? '' } }),

    getSchemaValidation: (apiKey: string | null, auditId: string) =>
      apiCall<SchemaValidationSummary>(`${SEO_SERVICE_URL}/api/crawl-data/${auditId}/schema-validation`, { headers: { 'X-API-Key': apiKey ?? '' } }),
  },

  // ── AI Settings (Phase E) ──────────────────────────────────────────────

  getAISettings: async (apiKey: string | null) =>
    apiCall<{
      keys: Record<string, string | null>;
      preferred_provider: string;
      openrouter_is_master: boolean;
      active_provider: string;
      models: Record<string, string>;
      providers: Record<string, string>;
    }>(`${USER_SERVICE_URL}/api/ai/settings`, { headers: { 'X-API-Key': apiKey ?? '' } }),

  updateAISettings: async (apiKey: string | null, data: Record<string, unknown>) =>
    apiCall<unknown>(`${USER_SERVICE_URL}/api/ai/settings`, {
      method: 'PUT', headers: { 'X-API-Key': apiKey ?? '' }, body: JSON.stringify(data),
    }),

  getAIModels: async (apiKey: string | null, provider?: string): Promise<Array<{
    id: string; provider: string; model_id: string; display_name: string; tier: string; is_active: boolean;
  }>> =>
    apiCall(`${USER_SERVICE_URL}/api/ai/models${provider ? `?provider=${encodeURIComponent(provider)}` : ''}`, { headers: { 'X-API-Key': apiKey ?? '' } }),

  testProvider: async (apiKey: string | null, provider: string, providerKey?: string) =>
    apiCall<{ provider: string; ok: boolean; latency_ms: number; detail: string | null; error: string | null }>(`${USER_SERVICE_URL}/api/ai/test-provider`, {
      method: 'POST', headers: { 'X-API-Key': apiKey ?? '', 'Content-Type': 'application/json' },
      body: JSON.stringify({ provider, ...(providerKey ? { api_key: providerKey } : {}) }),
    }),

  getProjectAISettings: async (apiKey: string | null, projectId: string) =>
    apiCall<{
      project_id: string; keys: Record<string, string | null>;
      preferred_provider: string | null; models: Record<string, string | null>;
      providers: Record<string, string | null>;
      is_overridden: boolean;
    }>(`${USER_SERVICE_URL}/api/ai/projects/${projectId}/ai-settings`, { headers: { 'X-API-Key': apiKey ?? '' } }),

  updateProjectAISettings: async (apiKey: string | null, projectId: string, data: Record<string, unknown>) =>
    apiCall<unknown>(`${USER_SERVICE_URL}/api/ai/projects/${projectId}/ai-settings`, {
      method: 'PUT', headers: { 'X-API-Key': apiKey ?? '' }, body: JSON.stringify(data),
    }),

  deleteProjectAISettings: async (apiKey: string | null, projectId: string) =>
    apiCall<void>(`${USER_SERVICE_URL}/api/ai/projects/${projectId}/ai-settings`, {
      method: 'DELETE', headers: { 'X-API-Key': apiKey ?? '' },
    }),
};


// ============================================================================
// Campaign API (SEA)
// ============================================================================

export interface Campaign {
  id: string;
  user_id: string;
  name: string;
  platform: 'google_ads' | 'bing_ads' | 'meta_ads' | 'tiktok_ads';
  status: 'active' | 'paused' | 'draft' | 'completed' | 'archived';
  campaign_type?: 'search' | 'shopping' | 'display' | 'pmax' | 'video' | null;
  bidding_strategy?: 'maximize_conversions' | 'maximize_clicks' | 'target_cpa' | 'target_roas' | 'manual_cpc' | null;
  target_cpa?: number | null;
  target_roas?: number | null;
  budget_daily: number | null;
  budget_total: number | null;
  start_date: string | null;
  end_date: string | null;
  google_ads_customer_id?: string | null;
  shopping_merchant_id?: string | null;
  settings?: Record<string, any> | null;
  external_id?: string | null;
  external_status?: string | null;
  impressions?: number;
  clicks?: number;
  conversions?: number;
  spend?: number;
  ctr?: number | null;
  avg_cpc?: number | null;
  last_synced_at?: string | null;
  created_at: string;
  updated_at: string;
}

// ── Meta Ads (Facebook Marketing API) ────────────────────────────────────────
export interface MetaAdAccount {
  id: string;
  account_id?: string | null;
  name?: string | null;
  account_status?: number | null;
  currency?: string | null;
  timezone_name?: string | null;
  business_name?: string | null;
}

export interface MetaCampaign {
  external_id?: string | null;
  name: string;
  status: string;
  budget_daily?: number | null;
  budget_total?: number | null;
  campaign_type?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  buying_type?: string | null;
}

export interface MetaAdSet {
  external_id?: string | null;
  name: string;
  status: string;
  budget_daily?: number | null;
  budget_total?: number | null;
  optimization_goal?: string | null;
  billing_event?: string | null;
  start_date?: string | null;
  end_date?: string | null;
}

export interface MetaAd {
  external_id?: string | null;
  name: string;
  status: string;
  creative_id?: string | null;
}

export interface MetaInsight {
  date_start?: string;
  date_stop?: string;
  spend?: string;
  impressions?: string;
  clicks?: string;
  ctr?: string;
  cpc?: string;
  reach?: string;
  frequency?: string;
  campaign_id?: string;
  campaign_name?: string;
  account_currency?: string;
}

export interface MetaCampaignCreateInput {
  account_id: string;
  name: string;
  objective?: string;
  status?: 'active' | 'paused' | 'removed' | 'archived';
  budget_daily?: number;
  budget_total?: number;
  buying_type?: string;
  special_ad_categories?: string[];
}

export const metaAdsAPI = {
  getAccounts: async (apiKey: string | null): Promise<MetaAdAccount[]> => {
    return apiCall<MetaAdAccount[]>(`${CAMPAIGN_SERVICE_URL}/api/meta-ads/accounts`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  verify: async (apiKey: string | null, accountId: string): Promise<{ success: boolean; count?: number; error?: string; error_code?: string }> => {
    return apiCall(`${CAMPAIGN_SERVICE_URL}/api/meta-ads/verify?account_id=${encodeURIComponent(accountId)}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getCampaigns: async (apiKey: string | null, accountId: string): Promise<MetaCampaign[]> => {
    return apiCall<MetaCampaign[]>(`${CAMPAIGN_SERVICE_URL}/api/meta-ads/campaigns?account_id=${encodeURIComponent(accountId)}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getAdSets: async (apiKey: string | null, accountId: string, campaignId: string): Promise<MetaAdSet[]> => {
    return apiCall<MetaAdSet[]>(`${CAMPAIGN_SERVICE_URL}/api/meta-ads/campaigns/${encodeURIComponent(campaignId)}/adsets?account_id=${encodeURIComponent(accountId)}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getAds: async (apiKey: string | null, accountId: string, adSetId: string): Promise<MetaAd[]> => {
    return apiCall<MetaAd[]>(`${CAMPAIGN_SERVICE_URL}/api/meta-ads/adsets/${encodeURIComponent(adSetId)}/ads?account_id=${encodeURIComponent(accountId)}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getInsights: async (apiKey: string | null, accountId: string, level: 'campaign' | 'adset' | 'ad' | 'account' = 'campaign', dateFrom?: string, dateTo?: string): Promise<MetaInsight[]> => {
    const params = new URLSearchParams({ account_id: accountId, level });
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    return apiCall<MetaInsight[]>(`${CAMPAIGN_SERVICE_URL}/api/meta-ads/insights?${params.toString()}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  createCampaign: async (apiKey: string | null, data: MetaCampaignCreateInput): Promise<Campaign> => {
    return apiCall<Campaign>(`${CAMPAIGN_SERVICE_URL}/api/meta-ads/campaigns`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  updateStatus: async (apiKey: string | null, campaignId: string, status: string): Promise<{ external_id: string; status: string }> => {
    return apiCall(`${CAMPAIGN_SERVICE_URL}/api/meta-ads/campaigns/${encodeURIComponent(campaignId)}/status`, {
      method: 'PATCH',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify({ status }),
    });
  },

  updateBudget: async (apiKey: string | null, campaignId: string, budgetDaily: number): Promise<{ external_id: string; budget_daily: number }> => {
    return apiCall(`${CAMPAIGN_SERVICE_URL}/api/meta-ads/campaigns/${encodeURIComponent(campaignId)}/budget`, {
      method: 'PATCH',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify({ budget_daily: budgetDaily }),
    });
  },
};

export interface CampaignSuggestionInput {
  product_service: string;
  target_audience?: string;
  usps?: string;
  platform: 'google_ads' | 'bing_ads' | 'meta_ads' | 'tiktok_ads';
  budget_daily?: number;
  tone?: 'professional' | 'friendly' | 'luxury' | 'urgent' | 'youthful';
  project_id?: string;
}

// ── Social Media Posts (social-media-service) ───────────────────────────────
export interface SocialPost {
  id: string;
  group_id: string;
  user_id: string;
  platform: string;
  status: 'draft' | 'scheduled' | 'publishing' | 'published' | 'failed';
  text?: string | null;
  media_asset_ids: string[];
  media_project_id?: string | null;
  scheduled_at?: string | null;
  published_at?: string | null;
  external_post_id?: string | null;
  error_message?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface ComposePostInput {
  platform: string;
  topic: string;
  tone?: string;
  hashtags?: boolean;
  cta?: string;
  image_prompt?: string;
  media_project_id?: string;
  scheduled_at?: string;
}

export interface ComposePostResult {
  post: SocialPost;
  text_source: string;
  media_job_id?: string | null;
  media_status?: string | null;
  media_asset_id?: string | null;
}

export interface PostMediaState {
  job_id?: string | null;
  job_type?: string | null;
  status?: string | null;
  asset_id?: string | null;
  prompt?: string | null;
  poll_error?: string;
}

export const socialMediaAPI = {
  compose: async (apiKey: string | null, data: ComposePostInput): Promise<ComposePostResult> => {
    return apiCall<ComposePostResult>(`${SOCIAL_MEDIA_SERVICE_URL}/api/posts/compose`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  listPosts: async (apiKey: string | null, params?: { platform?: string; status?: string }): Promise<SocialPost[]> => {
    const search = new URLSearchParams();
    if (params?.platform) search.set('platform', params.platform);
    if (params?.status) search.set('status', params.status);
    const qs = search.toString();
    return apiCall<SocialPost[]>(`${SOCIAL_MEDIA_SERVICE_URL}/api/posts${qs ? `?${qs}` : ''}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getPost: async (apiKey: string | null, postId: string): Promise<SocialPost> => {
    return apiCall<SocialPost>(`${SOCIAL_MEDIA_SERVICE_URL}/api/posts/${encodeURIComponent(postId)}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  updatePost: async (apiKey: string | null, postId: string, data: Partial<ComposePostInput> & { status?: string }): Promise<SocialPost> => {
    return apiCall<SocialPost>(`${SOCIAL_MEDIA_SERVICE_URL}/api/posts/${encodeURIComponent(postId)}`, {
      method: 'PATCH',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  deletePost: async (apiKey: string | null, postId: string): Promise<void> => {
    await apiCall<void>(`${SOCIAL_MEDIA_SERVICE_URL}/api/posts/${encodeURIComponent(postId)}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getPostMedia: async (apiKey: string | null, postId: string): Promise<{ media: PostMediaState[] }> => {
    return apiCall<{ media: PostMediaState[] }>(`${SOCIAL_MEDIA_SERVICE_URL}/api/posts/${encodeURIComponent(postId)}/media`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },
};

// ── TikTok Ads (Business API) ────────────────────────────────────────────────
export interface TikTokAdAccount {
  advertiser_id: string;
  name?: string | null;
}

export interface TikTokCampaign {
  external_id?: string | null;
  name: string;
  status: string;
  external_status?: string | null;
  budget_daily?: number | null;
  budget_total?: number | null;
  campaign_type?: string | null;
  start_date?: string | null;
  end_date?: string | null;
}

export interface TikTokAdGroup {
  external_id?: string | null;
  name: string;
  status: string;
  external_status?: string | null;
  max_cpc?: number | null;
}

export interface TikTokAd {
  external_id?: string | null;
  ad_type?: string | null;
  status: string;
  external_status?: string | null;
}

export interface TikTokCampaignCreateInput {
  advertiser_id: string;
  name: string;
  objective?: string;
  status?: 'active' | 'paused' | 'removed' | 'archived';
  budget_daily?: number;
  budget_total?: number;
}

export const tiktokAdsAPI = {
  getAccounts: async (apiKey: string | null): Promise<TikTokAdAccount[]> => {
    return apiCall<TikTokAdAccount[]>(`${CAMPAIGN_SERVICE_URL}/api/tiktok-ads/accounts`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  verify: async (apiKey: string | null, advertiserId: string): Promise<{ success: boolean; count?: number; error?: string; error_code?: string }> => {
    return apiCall(`${CAMPAIGN_SERVICE_URL}/api/tiktok-ads/verify?advertiser_id=${encodeURIComponent(advertiserId)}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getCampaigns: async (apiKey: string | null, advertiserId: string): Promise<TikTokCampaign[]> => {
    return apiCall<TikTokCampaign[]>(`${CAMPAIGN_SERVICE_URL}/api/tiktok-ads/campaigns?advertiser_id=${encodeURIComponent(advertiserId)}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getAdGroups: async (apiKey: string | null, advertiserId: string, campaignId: string): Promise<TikTokAdGroup[]> => {
    return apiCall<TikTokAdGroup[]>(`${CAMPAIGN_SERVICE_URL}/api/tiktok-ads/campaigns/${encodeURIComponent(campaignId)}/adgroups?advertiser_id=${encodeURIComponent(advertiserId)}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getAds: async (apiKey: string | null, advertiserId: string, adGroupId: string): Promise<TikTokAd[]> => {
    return apiCall<TikTokAd[]>(`${CAMPAIGN_SERVICE_URL}/api/tiktok-ads/adgroups/${encodeURIComponent(adGroupId)}/ads?advertiser_id=${encodeURIComponent(advertiserId)}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  sync: async (apiKey: string | null, advertiserId: string): Promise<{ campaigns: Record<string, number>; ad_groups: Record<string, number>; ads: Record<string, number>; keywords: Record<string, number> }> => {
    return apiCall(`${CAMPAIGN_SERVICE_URL}/api/tiktok-ads/sync?advertiser_id=${encodeURIComponent(advertiserId)}`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  createCampaign: async (apiKey: string | null, data: TikTokCampaignCreateInput): Promise<Campaign> => {
    return apiCall<Campaign>(`${CAMPAIGN_SERVICE_URL}/api/tiktok-ads/campaigns`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  updateStatus: async (apiKey: string | null, campaignId: string, status: string): Promise<{ external_id: string; status: string }> => {
    return apiCall(`${CAMPAIGN_SERVICE_URL}/api/tiktok-ads/campaigns/${encodeURIComponent(campaignId)}/status`, {
      method: 'PATCH',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify({ status }),
    });
  },

  updateBudget: async (apiKey: string | null, campaignId: string, budgetDaily: number): Promise<{ external_id: string; budget_daily: number }> => {
    return apiCall(`${CAMPAIGN_SERVICE_URL}/api/tiktok-ads/campaigns/${encodeURIComponent(campaignId)}/budget`, {
      method: 'PATCH',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify({ budget_daily: budgetDaily }),
    });
  },
};

export interface AdCopySuggestion {
  headline_1: string;
  headline_2: string;
  headline_3: string | null;
  description_1: string;
  description_2: string | null;
}

export interface CampaignSuggestion {
  campaign_name: string;
  keywords: string[];
  ad_copy: AdCopySuggestion[];
  bidding: {
    strategy: 'maximize_conversions' | 'maximize_clicks' | 'target_cpa' | 'target_roas' | 'manual_cpc';
    target_cpa: number | null;
    target_roas: number | null;
  };
  landing_page_hint: string;
  platform: string;
  ai_used?: boolean;
  model?: string | null;
}

export interface AdGroup {
  id: number;
  campaign_id: number;
  name: string;
  status: 'active' | 'paused';
  bid_amount: number | null;
  created_at: string;
}

export interface Ad {
  id: number;
  ad_group_id: number;
  headline: string;
  description: string;
  display_url: string;
  final_url: string;
  status: 'active' | 'paused' | 'rejected';
  impressions: number;
  clicks: number;
  conversions: number;
  cost: number;
  created_at: string;
}

export interface ABTest {
  id: number;
  campaign_id: number;
  name: string;
  status: 'running' | 'completed' | 'paused';
  variant_a: string;
  variant_b: string;
  winner: 'a' | 'b' | null;
  confidence: number | null;
  created_at: string;
}

// Performance & Reporting
export interface PerformanceSnapshot {
  id: string;
  campaign_id: string;
  date: string;
  granularity: string;
  impressions: number;
  clicks: number;
  conversions: number;
  cost_micros: number;
  conversion_value: number;
  ctr: number;
  avg_cpc: number;
  avg_cpm: number;
  roas: number | null;
  quality_score_avg: number | null;
  search_impression_share: number | null;
  device: string | null;
  network: string | null;
}

export interface SearchTermReport {
  id: string;
  campaign_id: string;
  search_term: string;
  matched_keyword: string | null;
  match_type: string | null;
  date: string;
  impressions: number;
  clicks: number;
  conversions: number;
  cost_micros: number;
  is_negative: boolean;
}

export interface AuctionInsight {
  id: string;
  campaign_id: string;
  competitor_domain: string;
  date_range_start: string;
  date_range_end: string;
  impression_share: number;
  overlap_rate: number;
  outranking_share: number;
  position_above_rate: number;
  top_of_page_rate: number;
  abs_top_of_page_rate: number;
}

// Keyword Research
export interface KeywordIdea {
  keyword: string;
  avg_monthly_searches: number | null;
  competition: string | null;
  low_bid_micros: number | null;
  high_bid_micros: number | null;
}

export interface NegativeKeyword {
  id: string;
  campaign_id: string | null;
  ad_group_id: string | null;
  keyword_text: string;
  match_type: string;
  source: string;
}

// Products & Merchant Center
export interface Product {
  id: string;
  user_id: string;
  merchant_id: string;
  offer_id: string;
  title: string;
  description: string | null;
  link: string | null;
  image_link: string | null;
  price_amount: number | null;
  price_currency: string;
  brand: string | null;
  condition: string;
  availability: string;
  approval_status: string | null;
  last_synced_at: string | null;
}

export interface ProductPerformance {
  id: string;
  product_id: string;
  date: string;
  impressions: number;
  clicks: number;
  cost_micros: number;
  conversions: number;
  conversion_value: number;
}

// Shopping & PMax
export interface AssetGroup {
  id: string;
  campaign_id: string;
  name: string;
  status: string;
  final_url: string | null;
  headlines: string | null;
  descriptions: string | null;
  images: string | null;
  business_name: string | null;
  external_id: string | null;
}

export interface ListingGroup {
  id: string;
  campaign_id: string;
  parent_id: string | null;
  dimension_type: string | null;
  dimension_value: string | null;
  is_subdivision: boolean;
  is_excluded: boolean;
  max_cpc: number | null;
}

// Audiences
export interface AudienceSegment {
  id: string;
  user_id: string;
  name: string;
  segment_type: string;
  description: string | null;
  source: string;
  external_id: string | null;
  size_estimate: number | null;
  status: string;
  membership_duration_days: number;
}

export interface CampaignAudience {
  id: string;
  campaign_id: string;
  audience_segment_id: string;
  targeting_mode: string;
  bid_modifier: number;
  is_excluded: boolean;
}

export interface RemarketingTag {
  id: string;
  user_id: string;
  name: string;
  tag_type: string;
  external_id: string | null;
  snippet: string | null;
  is_active: boolean;
}

// Optimization
export interface BidRule {
  id: string;
  user_id: string;
  name: string;
  campaign_id: string | null;
  ad_group_id: string | null;
  rule_type: string;
  conditions: string | null;
  actions: string | null;
  is_active: boolean;
  last_evaluated_at: string | null;
  last_triggered_at: string | null;
  created_at: string;
}

export interface Recommendation {
  id: string;
  user_id: string;
  campaign_id: string | null;
  category: string;
  title: string;
  description: string;
  impact_estimate: string | null;
  status: string;
  auto_applicable: boolean;
  created_at: string;
}

export interface BidRuleLog {
  id: string;
  rule_id: string;
  triggered: boolean;
  conditions_met: string | null;
  actions_taken: string | null;
  old_value: string | null;
  new_value: string | null;
  created_at: string;
}

// Budget Alerts & Overview
export interface BudgetAlert {
  campaign_id: string;
  campaign_name: string;
  level: 'warning' | 'critical';
  burn_rate: number;
  today_spend: number;
  budget_daily: number;
  avg_daily_spend: number;
  projected_monthly: number;
  message: string;
}

export interface BudgetOverview {
  total_budget: number;
  total_spent_today: number;
  total_projected_monthly: number;
  alerts_count: number;
  campaigns_count: number;
}

export interface NegativeKeywordSuggestion {
  search_term: string;
  campaign_id: string;
  campaign_name: string;
  reason: string;
  cost: number;
  impressions: number;
  clicks: number;
  conversions: number;
}

export const campaignAPI = {
  getCampaigns: async (apiKey: string | null): Promise<Campaign[]> => {
    const res = await apiCall<{ items: Campaign[] }>(`${CAMPAIGN_SERVICE_URL}/api/campaigns`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
    return res.items;
  },

  getCampaignOAuthStatus: async (apiKey: string | null): Promise<{ connected: boolean; status: string; account_email: string | null; error_message: string | null }> => {
    return apiCall(`${CAMPAIGN_SERVICE_URL}/api/campaigns/oauth-status`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  createCampaign: async (apiKey: string | null, data: Partial<Campaign>): Promise<Campaign> => {
    return apiCall<Campaign>(`${CAMPAIGN_SERVICE_URL}/api/campaigns`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  suggestCampaign: async (apiKey: string | null, data: CampaignSuggestionInput): Promise<CampaignSuggestion> => {
    return apiCall<CampaignSuggestion>(`${CAMPAIGN_SERVICE_URL}/api/campaigns/suggest`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  getAiUsageSummary: async (apiKey: string | null, projectId?: string): Promise<{
    user_id: string;
    project_id: string | null;
    total_cost_usd: number;
    total_input_tokens: number;
    total_output_tokens: number;
    total_calls: number;
    by_feature: Record<string, { calls: number; cost_usd: number; input_tokens: number; output_tokens: number; models: string[] }>;
  }> => {
    const q = projectId ? `?project_id=${projectId}` : '';
    return apiCall(`${CAMPAIGN_SERVICE_URL}/api/ai-usage/summary${q}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getCampaign: async (apiKey: string | null, campaignId: string): Promise<Campaign> => {
    return apiCall<Campaign>(`${CAMPAIGN_SERVICE_URL}/api/campaigns/${campaignId}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  updateCampaign: async (apiKey: string | null, campaignId: string, data: Partial<Campaign>): Promise<Campaign> => {
    return apiCall<Campaign>(`${CAMPAIGN_SERVICE_URL}/api/campaigns/${campaignId}`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  deleteCampaign: async (apiKey: string | null, campaignId: string): Promise<void> => {
    await apiCall<void>(`${CAMPAIGN_SERVICE_URL}/api/campaigns/${campaignId}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getAdGroups: async (apiKey?: string, campaignId?: number): Promise<AdGroup[]> => {
    const url = campaignId
      ? `${CAMPAIGN_SERVICE_URL}/api/ad-groups?campaign_id=${campaignId}`
      : `${CAMPAIGN_SERVICE_URL}/api/ad-groups`;
    const res = await apiCall<{ items: AdGroup[] }>(url, {
      ...(apiKey && { headers: { 'X-API-Key': apiKey ?? '' } }),
    });
    return res.items;
  },

  getAds: async (apiKey?: string, adGroupId?: number): Promise<Ad[]> => {
    const url = adGroupId
      ? `${CAMPAIGN_SERVICE_URL}/api/ads?ad_group_id=${adGroupId}`
      : `${CAMPAIGN_SERVICE_URL}/api/ads`;
    const res = await apiCall<{ items: Ad[] }>(url, {
      ...(apiKey && { headers: { 'X-API-Key': apiKey ?? '' } }),
    });
    return res.items;
  },

  getPerformance: async (apiKey: string | null, campaignId: string, days: number = 30): Promise<Record<string, unknown>> => {
    return apiCall<Record<string, unknown>>(`${CAMPAIGN_SERVICE_URL}/api/performance/${campaignId}?days=${days}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getABTests: async (apiKey?: string, campaignId?: number): Promise<ABTest[]> => {
    const url = campaignId
      ? `${CAMPAIGN_SERVICE_URL}/api/ab-tests?campaign_id=${campaignId}`
      : `${CAMPAIGN_SERVICE_URL}/api/ab-tests`;
    const res = await apiCall<{ items: ABTest[] }>(url, {
      ...(apiKey && { headers: { 'X-API-Key': apiKey ?? '' } }),
    });
    return res.items;
  },

  getBudgets: async (apiKey?: string, campaignId?: number): Promise<Record<string, unknown>[]> => {
    const url = campaignId
      ? `${CAMPAIGN_SERVICE_URL}/api/budgets?campaign_id=${campaignId}`
      : `${CAMPAIGN_SERVICE_URL}/api/budgets`;
    const res = await apiCall<{ items: Record<string, unknown>[] }>(url, {
      ...(apiKey && { headers: { 'X-API-Key': apiKey ?? '' } }),
    });
    return res.items;
  },

  getKeywords: async (apiKey?: string): Promise<Record<string, unknown>[]> => {
    const res = await apiCall<{ items: Record<string, unknown>[] }>(`${CAMPAIGN_SERVICE_URL}/api/keywords`, {
      ...(apiKey && { headers: { 'X-API-Key': apiKey ?? '' } }),
    });
    return res.items;
  },

  // --- CRUD: Ad Groups ---
  createAdGroup: async (apiKey: string | null, data: { campaign_id: string; name: string; max_cpc?: number }): Promise<AdGroup> => {
    return apiCall<AdGroup>(`${CAMPAIGN_SERVICE_URL}/api/ad-groups`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  updateAdGroup: async (apiKey: string | null, id: string, data: Partial<AdGroup>): Promise<AdGroup> => {
    return apiCall<AdGroup>(`${CAMPAIGN_SERVICE_URL}/api/ad-groups/${id}`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  deleteAdGroup: async (apiKey: string | null, id: string): Promise<void> => {
    await apiCall<void>(`${CAMPAIGN_SERVICE_URL}/api/ad-groups/${id}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  pushAdGroup: async (apiKey: string | null, adGroupId: string, customerId?: string, projectId?: string): Promise<AdGroup> => {
    const params = new URLSearchParams();
    if (customerId) params.set('customer_id', customerId);
    if (projectId) params.set('project_id', projectId);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return apiCall<AdGroup>(`${CAMPAIGN_SERVICE_URL}/api/ad-groups/${adGroupId}/push${qs}`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  // --- CRUD: Ads ---
  createAd: async (apiKey: string | null, data: Record<string, unknown>): Promise<Ad> => {
    return apiCall<Ad>(`${CAMPAIGN_SERVICE_URL}/api/ads`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  updateAd: async (apiKey: string | null, id: string, data: Partial<Ad>): Promise<Ad> => {
    return apiCall<Ad>(`${CAMPAIGN_SERVICE_URL}/api/ads/${id}`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  deleteAd: async (apiKey: string | null, id: string): Promise<void> => {
    await apiCall<void>(`${CAMPAIGN_SERVICE_URL}/api/ads/${id}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  pushAd: async (apiKey: string | null, adId: string, customerId?: string, projectId?: string): Promise<Ad> => {
    const params = new URLSearchParams();
    if (customerId) params.set('customer_id', customerId);
    if (projectId) params.set('project_id', projectId);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return apiCall<Ad>(`${CAMPAIGN_SERVICE_URL}/api/ads/${adId}/push${qs}`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  generateAdCopy: async (apiKey: string | null, data: { ad_group_id: string; product_name: string; product_description: string; target_audience?: string; tone?: string; num_variations?: number; project_id?: string; project_domain?: string }): Promise<Record<string, unknown>> => {
    return apiCall<Record<string, unknown>>(`${CAMPAIGN_SERVICE_URL}/api/ads/generate`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  // --- CRUD: Keywords ---
  createKeyword: async (apiKey: string | null, data: { ad_group_id: string; keyword: string; match_type: string; max_cpc?: number }): Promise<Record<string, unknown>> => {
    return apiCall<Record<string, unknown>>(`${CAMPAIGN_SERVICE_URL}/api/keywords`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  updateKeyword: async (apiKey: string | null, id: string, data: Record<string, unknown>): Promise<Record<string, unknown>> => {
    return apiCall<Record<string, unknown>>(`${CAMPAIGN_SERVICE_URL}/api/keywords/${id}`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  deleteKeyword: async (apiKey: string | null, id: string): Promise<void> => {
    await apiCall<void>(`${CAMPAIGN_SERVICE_URL}/api/keywords/${id}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  pushKeyword: async (apiKey: string | null, keywordId: string, customerId?: string, projectId?: string): Promise<Record<string, unknown>> => {
    const params = new URLSearchParams();
    if (customerId) params.set('customer_id', customerId);
    if (projectId) params.set('project_id', projectId);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return apiCall<Record<string, unknown>>(`${CAMPAIGN_SERVICE_URL}/api/keywords/${keywordId}/push${qs}`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  bulkImportKeywords: async (apiKey: string | null, data: { ad_group_id: string; keywords: Array<{ keyword: string; match_type: string; max_cpc?: number }> }): Promise<Record<string, unknown>> => {
    return apiCall<Record<string, unknown>>(`${CAMPAIGN_SERVICE_URL}/api/keywords/bulk`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  // --- CRUD: A/B Tests ---
  createABTest: async (apiKey: string | null, data: { campaign_id: string; name: string; variant_a: Record<string, unknown>; variant_b: Record<string, unknown> }): Promise<ABTest> => {
    return apiCall<ABTest>(`${CAMPAIGN_SERVICE_URL}/api/ab-tests`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  updateABTest: async (apiKey: string | null, id: string, data: Partial<ABTest>): Promise<ABTest> => {
    return apiCall<ABTest>(`${CAMPAIGN_SERVICE_URL}/api/ab-tests/${id}`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  deleteABTest: async (apiKey: string | null, id: string): Promise<void> => {
    await apiCall<void>(`${CAMPAIGN_SERVICE_URL}/api/ab-tests/${id}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  // --- Dashboard ---
  getCampaignDashboard: async (apiKey: string | null): Promise<Record<string, unknown>> => {
    return apiCall<Record<string, unknown>>(`${CAMPAIGN_SERVICE_URL}/api/dashboard`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  // --- Budget Alerts ---
  getCampaignBudgetAlerts: async (apiKey: string | null): Promise<{ alerts: Array<{ campaign_id: string; name: string; status: string; budget_daily: number; spend: number; utilization: number; external_id: string | null }>; total: number; threshold: number }> => {
    return apiCall(`${CAMPAIGN_SERVICE_URL}/api/campaigns/budget-alerts`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  // --- Budget Summary ---
  getBudgetSummary: async (apiKey: string | null, campaignId: string): Promise<Record<string, unknown>> => {
    return apiCall<Record<string, unknown>>(`${CAMPAIGN_SERVICE_URL}/api/budgets/summary/${campaignId}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getBudgetAlerts: async (apiKey: string | null): Promise<{ alerts: Array<{ campaign_id: string; name: string; status: string; budget_daily: number; spend: number; utilization: number; external_id: string | null }>; total: number; threshold: number }> => {
    return apiCall(`${CAMPAIGN_SERVICE_URL}/api/campaigns/budget-alerts`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getBudgetOverview: async (apiKey: string | null): Promise<BudgetOverview> => {
    return apiCall<BudgetOverview>(`${CAMPAIGN_SERVICE_URL}/api/budgets/overview`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getNegativeKeywordSuggestions: async (apiKey: string | null): Promise<NegativeKeywordSuggestion[]> => {
    return apiCall<NegativeKeywordSuggestion[]>(`${CAMPAIGN_SERVICE_URL}/api/budgets/negative-keyword-suggestions`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  // === Google Ads Sync ===
  syncCampaigns: async (apiKey: string | null, customerId: string): Promise<{ task_id: string; status: string }> => {
    return apiCall<{ task_id: string; status: string }>(`${CAMPAIGN_SERVICE_URL}/api/campaigns/sync?customer_id=${encodeURIComponent(customerId)}`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  pushCampaign: async (apiKey: string | null, campaignId: string, customerId?: string, projectId?: string): Promise<Record<string, unknown>> => {
    // customerId is optional — the backend falls back to campaign.google_ads_customer_id,
    // then to the central project-level ID (Project Settings → SEA) via projectId.
    const params = new URLSearchParams();
    if (customerId) params.set('customer_id', customerId);
    if (projectId) params.set('project_id', projectId);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return apiCall<Record<string, unknown>>(`${CAMPAIGN_SERVICE_URL}/api/campaigns/${campaignId}/push${qs}`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  activateCampaign: async (apiKey: string | null, campaignId: string, projectId?: string): Promise<Record<string, unknown>> => {
    const qs = projectId ? `?project_id=${encodeURIComponent(projectId)}` : '';
    return apiCall<Record<string, unknown>>(`${CAMPAIGN_SERVICE_URL}/api/campaigns/${campaignId}/activate${qs}`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  pauseCampaign: async (apiKey: string | null, campaignId: string, projectId?: string): Promise<Record<string, unknown>> => {
    const qs = projectId ? `?project_id=${encodeURIComponent(projectId)}` : '';
    return apiCall<Record<string, unknown>>(`${CAMPAIGN_SERVICE_URL}/api/campaigns/${campaignId}/pause${qs}`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  pullCampaignStatus: async (apiKey: string | null, campaignId: string, projectId?: string): Promise<Record<string, unknown>> => {
    const qs = projectId ? `?project_id=${encodeURIComponent(projectId)}` : '';
    return apiCall<Record<string, unknown>>(`${CAMPAIGN_SERVICE_URL}/api/campaigns/${campaignId}/pull-status${qs}`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  updateCampaignBudget: async (apiKey: string | null, campaignId: string, budgetDaily: number, projectId?: string): Promise<Record<string, unknown>> => {
    const params = new URLSearchParams();
    params.set('budget_daily', String(budgetDaily));
    if (projectId) params.set('project_id', projectId);
    return apiCall<Record<string, unknown>>(`${CAMPAIGN_SERVICE_URL}/api/campaigns/${campaignId}/budget?${params.toString()}`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  updateCampaignBidding: async (apiKey: string | null, campaignId: string, data: { strategy: string; target_cpa?: number; target_roas?: number }, projectId?: string): Promise<Record<string, unknown>> => {
    const params = new URLSearchParams();
    params.set('strategy', data.strategy);
    if (data.target_cpa != null) params.set('target_cpa', String(data.target_cpa));
    if (data.target_roas != null) params.set('target_roas', String(data.target_roas));
    if (projectId) params.set('project_id', projectId);
    return apiCall<Record<string, unknown>>(`${CAMPAIGN_SERVICE_URL}/api/campaigns/${campaignId}/bidding?${params.toString()}`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  // === Search Terms & Auction Insights ===
  getSearchTerms: async (apiKey: string | null, campaignId: string, startDate?: string, endDate?: string): Promise<SearchTermReport[]> => {
    const params = new URLSearchParams();
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return apiCall<SearchTermReport[]>(`${CAMPAIGN_SERVICE_URL}/api/campaigns/${campaignId}/search-terms${qs}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  syncSearchTerms: async (apiKey: string | null, campaignId: string, projectId?: string): Promise<{ total: number; created: number; updated: number }> => {
    const qs = projectId ? `?project_id=${encodeURIComponent(projectId)}` : '';
    return apiCall<{ total: number; created: number; updated: number }>(`${CAMPAIGN_SERVICE_URL}/api/campaigns/${campaignId}/search-terms/sync${qs}`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getAuctionInsights: async (apiKey: string | null, campaignId: string): Promise<AuctionInsight[]> => {
    return apiCall<AuctionInsight[]>(`${CAMPAIGN_SERVICE_URL}/api/campaigns/${campaignId}/auction-insights`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  syncAuctionInsights: async (apiKey: string | null, campaignId: string, projectId?: string): Promise<{ total: number; created: number; updated: number }> => {
    const qs = projectId ? `?project_id=${encodeURIComponent(projectId)}` : '';
    return apiCall<{ total: number; created: number; updated: number }>(`${CAMPAIGN_SERVICE_URL}/api/campaigns/${campaignId}/auction-insights/sync${qs}`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  syncCampaignPerformance: async (apiKey: string | null, campaignId: string, projectId?: string, daysBack: number = 30): Promise<{ total: number; created: number; updated: number }> => {
    const params = new URLSearchParams();
    if (projectId) params.set('project_id', projectId);
    params.set('days_back', String(daysBack));
    return apiCall<{ total: number; created: number; updated: number }>(`${CAMPAIGN_SERVICE_URL}/api/campaigns/${campaignId}/performance/sync?${params.toString()}`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  // === Keyword Research ===
  researchKeywords: async (apiKey: string | null, customerId: string, seedKeywords: string[], languageId?: string, geoTargetId?: string): Promise<KeywordIdea[]> => {
    return apiCall<KeywordIdea[]>(`${CAMPAIGN_SERVICE_URL}/api/keywords/research?customer_id=${encodeURIComponent(customerId)}`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify({ seed_keywords: seedKeywords, language_id: languageId || '1000', geo_target_id: geoTargetId || '2276' }),
    });
  },

  expandKeywordsAI: async (apiKey: string | null, seedKeywords: string[], industry?: string, language?: string): Promise<Record<string, unknown>[]> => {
    return apiCall<Record<string, unknown>[]>(`${CAMPAIGN_SERVICE_URL}/api/keywords/expand`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify({ seed_keywords: seedKeywords, industry: industry || '', language: language || 'en' }),
    });
  },

  // === Negative Keywords ===
  getNegativeKeywords: async (apiKey: string | null, campaignId: string): Promise<NegativeKeyword[]> => {
    return apiCall<NegativeKeyword[]>(`${CAMPAIGN_SERVICE_URL}/api/campaigns/${campaignId}/negative-keywords`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  addNegativeKeyword: async (apiKey: string | null, campaignId: string, data: { keyword_text: string; match_type?: string }): Promise<NegativeKeyword> => {
    return apiCall<NegativeKeyword>(`${CAMPAIGN_SERVICE_URL}/api/campaigns/${campaignId}/negative-keywords`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  deleteNegativeKeyword: async (apiKey: string | null, keywordId: string): Promise<{ status: string }> => {
    return apiCall<{ status: string }>(`${CAMPAIGN_SERVICE_URL}/api/negative-keywords/${keywordId}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  pushNegativeKeyword: async (apiKey: string | null, keywordId: string, projectId?: string): Promise<Record<string, unknown>> => {
    const qs = projectId ? `?project_id=${encodeURIComponent(projectId)}` : '';
    return apiCall<Record<string, unknown>>(`${CAMPAIGN_SERVICE_URL}/api/negative-keywords/${keywordId}/push${qs}`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  suggestNegativeKeywords: async (apiKey: string | null, campaignId: string): Promise<Record<string, unknown>[]> => {
    return apiCall<Record<string, unknown>[]>(`${CAMPAIGN_SERVICE_URL}/api/campaigns/${campaignId}/suggest-negatives`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  // === Products & Merchant Center ===
  getProducts: async (apiKey: string | null, page?: number, availability?: string): Promise<Product[]> => {
    const params = new URLSearchParams();
    if (page) params.set('page', String(page));
    if (availability) params.set('availability', availability);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return apiCall<Product[]>(`${CAMPAIGN_SERVICE_URL}/api/products${qs}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  syncProducts: async (apiKey: string | null, merchantId?: string): Promise<{ created: number; updated: number; total: number }> => {
    const qs = merchantId ? `?merchant_id=${encodeURIComponent(merchantId)}` : '';
    return apiCall<{ created: number; updated: number; total: number }>(`${CAMPAIGN_SERVICE_URL}/api/products/sync${qs}`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  createProduct: async (apiKey: string | null, data: Record<string, unknown>): Promise<Product> => {
    return apiCall<Product>(`${CAMPAIGN_SERVICE_URL}/api/products`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  getProductPerformance: async (apiKey: string | null, productId: string): Promise<ProductPerformance[]> => {
    return apiCall<ProductPerformance[]>(`${CAMPAIGN_SERVICE_URL}/api/products/${productId}/performance`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getProductStatuses: async (apiKey: string | null, merchantId?: string): Promise<Record<string, unknown>[]> => {
    const qs = merchantId ? `?merchant_id=${encodeURIComponent(merchantId)}` : '';
    return apiCall<Record<string, unknown>[]>(`${CAMPAIGN_SERVICE_URL}/api/products/statuses${qs}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  // === Shopping & PMax Campaigns ===
  createShoppingCampaign: async (apiKey: string | null, customerId: string, data: Record<string, unknown>): Promise<Record<string, unknown>> => {
    return apiCall<Record<string, unknown>>(`${CAMPAIGN_SERVICE_URL}/api/shopping/campaigns?customer_id=${encodeURIComponent(customerId)}`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  createPMaxCampaign: async (apiKey: string | null, customerId: string, data: Record<string, unknown>): Promise<Record<string, unknown>> => {
    return apiCall<Record<string, unknown>>(`${CAMPAIGN_SERVICE_URL}/api/pmax/campaigns?customer_id=${encodeURIComponent(customerId)}`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  getAssetGroups: async (apiKey: string | null, campaignId: string): Promise<AssetGroup[]> => {
    return apiCall<AssetGroup[]>(`${CAMPAIGN_SERVICE_URL}/api/campaigns/${campaignId}/asset-groups`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  createAssetGroup: async (apiKey: string | null, campaignId: string, data: Record<string, unknown>): Promise<AssetGroup> => {
    return apiCall<AssetGroup>(`${CAMPAIGN_SERVICE_URL}/api/campaigns/${campaignId}/asset-groups`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  pushAssetGroup: async (apiKey: string | null, assetGroupId: string, projectId?: string): Promise<Record<string, unknown>> => {
    const qs = projectId ? `?project_id=${encodeURIComponent(projectId)}` : '';
    return apiCall<Record<string, unknown>>(`${CAMPAIGN_SERVICE_URL}/api/asset-groups/${assetGroupId}/push${qs}`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getListingGroups: async (apiKey: string | null, campaignId: string): Promise<ListingGroup[]> => {
    return apiCall<ListingGroup[]>(`${CAMPAIGN_SERVICE_URL}/api/campaigns/${campaignId}/listing-groups`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  createListingGroup: async (apiKey: string | null, campaignId: string, data: Record<string, unknown>): Promise<ListingGroup> => {
    return apiCall<ListingGroup>(`${CAMPAIGN_SERVICE_URL}/api/campaigns/${campaignId}/listing-groups`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  // === Audiences ===
  getAudiences: async (apiKey: string | null, segmentType?: string): Promise<AudienceSegment[]> => {
    const qs = segmentType ? `?segment_type=${encodeURIComponent(segmentType)}` : '';
    const res = await apiCall<{ items: AudienceSegment[] }>(`${CAMPAIGN_SERVICE_URL}/api/audiences${qs}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
    return res.items;
  },

  createAudience: async (apiKey: string | null, data: { name: string; segment_type: string; description?: string; criteria?: Record<string, unknown>; membership_duration_days?: number }): Promise<AudienceSegment> => {
    return apiCall<AudienceSegment>(`${CAMPAIGN_SERVICE_URL}/api/audiences`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  deleteAudience: async (apiKey: string | null, segmentId: string): Promise<{ status: string }> => {
    return apiCall<{ status: string }>(`${CAMPAIGN_SERVICE_URL}/api/audiences/${segmentId}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getCampaignAudiences: async (apiKey: string | null, campaignId: string): Promise<CampaignAudience[]> => {
    return apiCall<CampaignAudience[]>(`${CAMPAIGN_SERVICE_URL}/api/campaigns/${campaignId}/audiences`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  attachAudience: async (apiKey: string | null, campaignId: string, data: { audience_segment_id: string; targeting_mode?: string; bid_modifier?: number }): Promise<CampaignAudience> => {
    return apiCall<CampaignAudience>(`${CAMPAIGN_SERVICE_URL}/api/campaigns/${campaignId}/audiences`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  removeCampaignAudience: async (apiKey: string | null, caId: string): Promise<{ status: string }> => {
    return apiCall<{ status: string }>(`${CAMPAIGN_SERVICE_URL}/api/campaign-audiences/${caId}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getRemarketingTags: async (apiKey: string | null): Promise<RemarketingTag[]> => {
    return apiCall<RemarketingTag[]>(`${CAMPAIGN_SERVICE_URL}/api/remarketing-tags`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  createRemarketingTag: async (apiKey: string | null, data: { name: string; tag_type: string; snippet?: string }): Promise<RemarketingTag> => {
    return apiCall<RemarketingTag>(`${CAMPAIGN_SERVICE_URL}/api/remarketing-tags`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  deleteRemarketingTag: async (apiKey: string | null, tagId: string): Promise<{ status: string }> => {
    return apiCall<{ status: string }>(`${CAMPAIGN_SERVICE_URL}/api/remarketing-tags/${tagId}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  // === Conversion Tracking ===
  getConversionActions: async (apiKey: string | null): Promise<Record<string, unknown>[]> => {
    return apiCall<Record<string, unknown>[]>(`${CAMPAIGN_SERVICE_URL}/api/conversion-actions`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  createConversionAction: async (apiKey: string | null, data: { name: string; category: string; campaign_id?: string }): Promise<Record<string, unknown>> => {
    return apiCall<Record<string, unknown>>(`${CAMPAIGN_SERVICE_URL}/api/conversion-actions`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  deleteConversionAction: async (apiKey: string | null, actionId: string): Promise<{ status: string }> => {
    return apiCall<{ status: string }>(`${CAMPAIGN_SERVICE_URL}/api/conversion-actions/${actionId}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  pushConversionAction: async (apiKey: string | null, actionId: string, projectId?: string): Promise<{ external_id: string | null }> => {
    const qs = projectId ? `?project_id=${encodeURIComponent(projectId)}` : '';
    return apiCall<{ external_id: string | null }>(`${CAMPAIGN_SERVICE_URL}/api/conversion-actions/${actionId}/push${qs}`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  // === Bid Rules & Optimization ===
  getBidRules: async (apiKey: string | null, campaignId?: string): Promise<BidRule[]> => {
    const qs = campaignId ? `?campaign_id=${encodeURIComponent(campaignId)}` : '';
    return apiCall<BidRule[]>(`${CAMPAIGN_SERVICE_URL}/api/bid-rules${qs}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  createBidRule: async (apiKey: string | null, data: { name: string; rule_type: string; campaign_id?: string; conditions?: Record<string, unknown>; actions?: Record<string, unknown> }): Promise<BidRule> => {
    return apiCall<BidRule>(`${CAMPAIGN_SERVICE_URL}/api/bid-rules`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  updateBidRule: async (apiKey: string | null, ruleId: string, data: Record<string, unknown>): Promise<BidRule> => {
    return apiCall<BidRule>(`${CAMPAIGN_SERVICE_URL}/api/bid-rules/${ruleId}`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  deleteBidRule: async (apiKey: string | null, ruleId: string): Promise<{ status: string }> => {
    return apiCall<{ status: string }>(`${CAMPAIGN_SERVICE_URL}/api/bid-rules/${ruleId}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  evaluateBidRules: async (apiKey: string | null): Promise<{ evaluated: number; results: Record<string, unknown>[] }> => {
    return apiCall<{ evaluated: number; results: Record<string, unknown>[] }>(`${CAMPAIGN_SERVICE_URL}/api/bid-rules/evaluate`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getBidRuleLogs: async (apiKey: string | null, ruleId: string): Promise<BidRuleLog[]> => {
    return apiCall<BidRuleLog[]>(`${CAMPAIGN_SERVICE_URL}/api/bid-rules/${ruleId}/logs`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getRecommendations: async (apiKey: string | null, status?: string, category?: string, campaignId?: string): Promise<Recommendation[]> => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (category) params.set('category', category);
    if (campaignId) params.set('campaign_id', campaignId);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return apiCall<Recommendation[]>(`${CAMPAIGN_SERVICE_URL}/api/recommendations${qs}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  generateRecommendations: async (apiKey: string | null): Promise<{ generated: number }> => {
    return apiCall<{ generated: number }>(`${CAMPAIGN_SERVICE_URL}/api/recommendations/generate`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  syncRecommendations: async (apiKey: string | null, projectId?: string): Promise<{ total: number; created: number; skipped: number }> => {
    const qs = projectId ? `?project_id=${encodeURIComponent(projectId)}` : '';
    return apiCall<{ total: number; created: number; skipped: number }>(`${CAMPAIGN_SERVICE_URL}/api/recommendations/sync${qs}`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  applyRecommendation: async (apiKey: string | null, recId: string, projectId?: string): Promise<{ status: string }> => {
    const qs = projectId ? `?project_id=${encodeURIComponent(projectId)}` : '';
    return apiCall<{ status: string }>(`${CAMPAIGN_SERVICE_URL}/api/recommendations/${recId}/apply${qs}`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  dismissRecommendation: async (apiKey: string | null, recId: string): Promise<{ status: string }> => {
    return apiCall<{ status: string }>(`${CAMPAIGN_SERVICE_URL}/api/recommendations/${recId}/dismiss`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },
};

// ============================================================================
// Automation API
// ============================================================================

export interface Workflow {
  id: string;
  user_id: string;
  name: string;
  description?: string;
  trigger_type: string;
  trigger_config: Record<string, unknown>;
  actions: Record<string, unknown>[];
  status: 'active' | 'paused' | 'draft';
  next_run?: string;
  created_at: string;
  updated_at: string;
}

export interface ContentPiece {
  id: string;
  user_id: string;
  project_id?: string;
  content_type: 'blog_post' | 'meta_description' | 'ad_copy' | 'social_post' | 'email';
  title: string;
  content: string;
  keywords?: Record<string, unknown>;
  seo_score?: number;
  ai_model_used?: string;
  prompt_used?: string;
  status: 'draft' | 'review' | 'published' | 'approved';
  created_at: string;
  updated_at: string;
}

export interface Report {
  id: string;
  user_id: string;
  title: string;
  report_type: string;
  report_subtype?: string;
  project_id?: string;
  configuration?: Record<string, unknown>;
  format: 'pdf' | 'html' | 'csv';
  schedule?: string;
  schedule_id?: string;
  sections?: string[];
  language?: string;
  date_from?: string;
  date_to?: string;
  last_generated?: string;
  created_at: string;
  updated_at: string;
}

export interface ReportExecution {
  id: string;
  report_id: string;
  status: 'generating' | 'completed' | 'failed';
  file_path?: string;
  file_size?: number;
  error?: string;
  generated_at: string;
}

export interface ReportSchedule {
  id: string;
  project_id: string;
  report_type: string;
  report_subtype: string;
  title: string;
  sections?: string[];
  language: string;
  frequency: 'manual' | 'weekly' | 'monthly' | 'quarterly';
  cron_expression?: string;
  time_of_day?: string;
  delivery_method: 'download' | 'email' | 'both';
  recipients?: string[];
  is_active: boolean;
  last_run?: string;
  next_run?: string;
  created_at: string;
}

export interface TriggerLog {
  id: string;
  workflow_id: string;
  trigger_type: string;
  status: string;
  details: Record<string, unknown> | null;
  created_at: string;
}

export const automationAPI = {
  // Workflows
  getWorkflows: async (apiKey: string | null): Promise<Workflow[]> => {
    const res = await apiCall<{ items: Workflow[] }>(`${AUTOMATION_SERVICE_URL}/api/workflows`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
    return res.items;
  },

  createWorkflow: async (apiKey: string | null, data: Partial<Workflow>): Promise<Workflow> => {
    return apiCall<Workflow>(`${AUTOMATION_SERVICE_URL}/api/workflows`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  updateWorkflow: async (apiKey: string | null, id: string, data: Partial<Workflow>): Promise<Workflow> => {
    return apiCall<Workflow>(`${AUTOMATION_SERVICE_URL}/api/workflows/${id}`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  executeWorkflow: async (apiKey: string | null, workflowId: string): Promise<{ message: string }> => {
    return apiCall<{ message: string }>(`${AUTOMATION_SERVICE_URL}/api/workflows/${workflowId}/execute`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  deleteWorkflow: async (apiKey: string | null, workflowId: string): Promise<void> => {
    await apiCall<void>(`${AUTOMATION_SERVICE_URL}/api/workflows/${workflowId}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  // Content
  getContent: async (apiKey: string | null): Promise<ContentPiece[]> => {
    const res = await apiCall<{ items: ContentPiece[] }>(`${AUTOMATION_SERVICE_URL}/api/content`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
    return res.items;
  },

  generateContent: async (apiKey: string | null, data: { content_type: string; title: string; keywords?: Record<string, unknown>; prompt?: string; project_id?: string }): Promise<{ message: string }> => {
    return apiCall<{ message: string }>(`${AUTOMATION_SERVICE_URL}/api/content/generate`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  // AI Chat (synchronous)
  aiChat: async (apiKey: string | null, data: { message: string; content_type: string; project_id?: string }): Promise<{ content: string; model_used: string }> => {
    return apiCall<{ content: string; model_used: string }>(`${AUTOMATION_SERVICE_URL}/api/ai/chat`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  // Reports
  getReports: async (apiKey: string | null): Promise<Report[]> => {
    const res = await apiCall<{ items: Report[] }>(`${AUTOMATION_SERVICE_URL}/api/reports`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
    return res.items;
  },

  generateReport: async (apiKey: string | null, data: { title: string; report_type: string; format: string; project_id?: string }): Promise<Report> => {
    return apiCall<Report>(`${AUTOMATION_SERVICE_URL}/api/reports`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  // Executions
  getExecutions: async (apiKey: string | null, workflowId?: string): Promise<Record<string, unknown>[]> => {
    const url = workflowId
      ? `${AUTOMATION_SERVICE_URL}/api/workflows/${workflowId}/executions`
      : `${AUTOMATION_SERVICE_URL}/api/executions`;
    const res = await apiCall<{ items: Record<string, unknown>[] }>(url, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
    return res.items;
  },

  // Templates
  getTemplates: async (apiKey: string | null): Promise<Record<string, unknown>[]> => {
    const res = await apiCall<{ items: Record<string, unknown>[] }>(`${AUTOMATION_SERVICE_URL}/api/templates`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
    return res.items;
  },

  createTemplate: async (apiKey: string | null, data: Record<string, unknown>): Promise<Record<string, unknown>> => {
    return apiCall<Record<string, unknown>>(`${AUTOMATION_SERVICE_URL}/api/templates`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  updateTemplate: async (apiKey: string | null, id: string, data: Record<string, unknown>): Promise<Record<string, unknown>> => {
    return apiCall<Record<string, unknown>>(`${AUTOMATION_SERVICE_URL}/api/templates/${id}`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  deleteTemplate: async (apiKey: string | null, id: string): Promise<void> => {
    await apiCall<void>(`${AUTOMATION_SERVICE_URL}/api/templates/${id}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  getDashboard: async (apiKey: string | null): Promise<Record<string, unknown>> => {
    return apiCall<Record<string, unknown>>(`${AUTOMATION_SERVICE_URL}/api/dashboard`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  // --- Execution management ---
  cancelExecution: async (apiKey: string | null, id: string): Promise<Record<string, unknown>> => {
    return apiCall<Record<string, unknown>>(`${AUTOMATION_SERVICE_URL}/api/executions/${id}/cancel`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  // --- Content CRUD ---
  updateContent: async (apiKey: string | null, id: string, data: Partial<ContentPiece>): Promise<ContentPiece> => {
    return apiCall<ContentPiece>(`${AUTOMATION_SERVICE_URL}/api/content/${id}`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  deleteContent: async (apiKey: string | null, id: string): Promise<void> => {
    await apiCall<void>(`${AUTOMATION_SERVICE_URL}/api/content/${id}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  updateReport: async (apiKey: string | null, id: string, data: Partial<Report>): Promise<Report> => {
    return apiCall<Report>(`${AUTOMATION_SERVICE_URL}/api/reports/${id}`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  deleteReport: async (apiKey: string | null, id: string): Promise<void> => {
    await apiCall<void>(`${AUTOMATION_SERVICE_URL}/api/reports/${id}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  // Trigger History
  getTriggerHistory: async (
    apiKey: string | null,
    workflowId: string,
    page = 1,
    perPage = 20,
  ): Promise<{ items: TriggerLog[]; total: number; page: number; per_page: number; pages: number }> => {
    return apiCall<{ items: TriggerLog[]; total: number; page: number; per_page: number; pages: number }>(
      `${AUTOMATION_SERVICE_URL}/api/webhooks/triggers/${workflowId}/history?page=${page}&per_page=${perPage}`,
      { headers: { 'X-API-Key': apiKey ?? '' } }
    );
  },

  fireWebhookTrigger: async (
    apiKey: string | null,
    eventType: string,
    payload: Record<string, unknown> = {},
  ): Promise<{ event_type: string; matched: number; fired: number }> => {
    return apiCall<{ event_type: string; matched: number; fired: number }>(
      `${AUTOMATION_SERVICE_URL}/api/webhooks/trigger`,
      {
        method: 'POST',
        headers: { 'X-API-Key': apiKey ?? '' },
        body: JSON.stringify({ event_type: eventType, payload }),
      }
    );
  },

  // Report executions
  getReportExecutions: async (apiKey: string | null, reportId: string): Promise<ReportExecution[]> => {
    return apiCall<ReportExecution[]>(
      `${AUTOMATION_SERVICE_URL}/api/reports/${reportId}/executions`,
      { headers: { 'X-API-Key': apiKey ?? '' } }
    );
  },

  triggerReport: async (apiKey: string | null, reportId: string): Promise<{ message: string }> => {
    return apiCall<{ message: string }>(
      `${AUTOMATION_SERVICE_URL}/api/reports/${reportId}/generate`,
      { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' } }
    );
  },

  downloadReportExecution: async (apiKey: string | null, reportId: string, executionId: string): Promise<Blob> => {
    const response = await fetch(
      `${AUTOMATION_SERVICE_URL}/api/reports/${reportId}/executions/${executionId}/download`,
      { headers: { 'X-API-Key': apiKey ?? '' } }
    );
    if (!response.ok) {
      throw new APIError(response.status, 'Download failed');
    }
    return response.blob();
  },

  // Quick-export: synchronous PDF generation for a page snapshot
  quickExport: async (apiKey: string | null, projectId: string, page: string): Promise<Blob> => {
    const response = await fetch(`${AUTOMATION_SERVICE_URL}/api/reports/quick-export`, {
      method: 'POST',
      headers: {
        'X-API-Key': apiKey ?? '',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ project_id: projectId, page }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: 'Export failed' }));
      throw new APIError(response.status, err.detail || 'Export failed');
    }
    return response.blob();
  },

  // Report schedules
  getReportSchedules: async (apiKey: string | null, projectId?: string): Promise<ReportSchedule[]> => {
    const params = projectId ? `?project_id=${projectId}` : '';
    return apiCall<ReportSchedule[]>(
      `${AUTOMATION_SERVICE_URL}/api/report-schedules${params}`,
      { headers: { 'X-API-Key': apiKey ?? '' } }
    );
  },

  createReportSchedule: async (apiKey: string | null, data: Omit<ReportSchedule, 'id' | 'is_active' | 'last_run' | 'next_run' | 'created_at'>): Promise<ReportSchedule> => {
    return apiCall<ReportSchedule>(`${AUTOMATION_SERVICE_URL}/api/report-schedules`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  updateReportSchedule: async (apiKey: string | null, id: string, data: Partial<ReportSchedule>): Promise<ReportSchedule> => {
    return apiCall<ReportSchedule>(`${AUTOMATION_SERVICE_URL}/api/report-schedules/${id}`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  deleteReportSchedule: async (apiKey: string | null, id: string): Promise<void> => {
    await apiCall<void>(`${AUTOMATION_SERVICE_URL}/api/report-schedules/${id}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  runScheduleNow: async (apiKey: string | null, id: string): Promise<{ message: string; task_id: string }> => {
    return apiCall<{ message: string; task_id: string }>(
      `${AUTOMATION_SERVICE_URL}/api/report-schedules/${id}/run-now`,
      { method: 'POST', headers: { 'X-API-Key': apiKey ?? '' } }
    );
  },
};

// ============================================================================
// Settings API
// ============================================================================

export interface GlobalSetting {
  id: number;
  setting_key: string;
  setting_value: string | null;
  setting_type: 'STRING' | 'NUMBER' | 'BOOLEAN' | 'JSON' | 'ENCRYPTED';
  category: 'API' | 'PROXY' | 'CRAWLER' | 'GENERAL' | 'LOGGING' | 'EMAIL' | 'SEO' | 'SEA' | 'MARKETING';
  description: string | null;
  is_sensitive: boolean;
  has_value: boolean;
  source: string | null;
  resolved_value: string | null;
  updated_at: string | null;
}

export interface ProjectSetting {
  setting_key: string;
  setting_value: string | null;
  setting_type: GlobalSetting['setting_type'];
  category: GlobalSetting['category'];
  description: string | null;
  is_sensitive: boolean;
  has_override: boolean;
  source: string | null;
  resolved_value: string | null;
  global_resolved_value: string | null;
  updated_at: string | null;
}

export const settingsAPI = {
  getAll: async (apiKey: string | null): Promise<{ settings: GlobalSetting[] }> => {
    return apiCall<{ settings: GlobalSetting[] }>(`${USER_SERVICE_URL}/api/settings`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  update: async (apiKey: string | null, key: string, value: string | null): Promise<GlobalSetting> => {
    return apiCall<GlobalSetting>(`${USER_SERVICE_URL}/api/settings/${key}`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify({ setting_value: value }),
    });
  },

  // ── Project-level settings (overrides on top of global) ──────────────
  getForProject: async (
    apiKey: string | null,
    projectId: string,
    category?: string
  ): Promise<{ settings: ProjectSetting[] }> => {
    const q = category ? `?category=${encodeURIComponent(category)}` : '';
    return apiCall<{ settings: ProjectSetting[] }>(
      `${USER_SERVICE_URL}/api/projects/${projectId}/settings${q}`,
      { headers: { 'X-API-Key': apiKey ?? '' } }
    );
  },

  updateForProject: async (
    apiKey: string | null,
    projectId: string,
    key: string,
    value: string | null
  ): Promise<ProjectSetting> => {
    return apiCall<ProjectSetting>(
      `${USER_SERVICE_URL}/api/projects/${projectId}/settings/${key}`,
      {
        method: 'PUT',
        headers: { 'X-API-Key': apiKey ?? '' },
        body: JSON.stringify({ setting_value: value }),
      }
    );
  },

  resetForProject: async (
    apiKey: string | null,
    projectId: string,
    key: string
  ): Promise<void> => {
    await apiCall<void>(
      `${USER_SERVICE_URL}/api/projects/${projectId}/settings/${key}`,
      { method: 'DELETE', headers: { 'X-API-Key': apiKey ?? '' } }
    );
  },
};

// ============================================================================
// Health Check API
// ============================================================================

export interface HealthStatus {
  status: string;
  service: string;
  database?: string;
}

export const healthAPI = {
  checkAll: async (): Promise<Record<string, HealthStatus>> => {
    const services = [
      { name: 'user-service', url: USER_SERVICE_URL },
      { name: 'seo-audit-service', url: SEO_SERVICE_URL },
      { name: 'campaign-service', url: CAMPAIGN_SERVICE_URL },
      { name: 'automation-service', url: AUTOMATION_SERVICE_URL },
      { name: 'logger-service', url: LOGGER_SERVICE_URL },
    ];

    const results = await Promise.allSettled(
      services.map(s => apiCall<HealthStatus>(`${s.url}/api/health`))
    );

    const status: Record<string, HealthStatus> = {};
    services.forEach((s, i) => {
      status[s.name] = results[i].status === 'fulfilled'
        ? (results[i] as PromiseFulfilledResult<HealthStatus>).value
        : { status: 'error', service: s.name };
    });
    return status;
  },
};

// ============================================================================
// Projects API (manages user projects with integrations)
// ============================================================================

import type {
  Project,
  NewProjectFormData,
  IntegrationProvider,
  Integration,
  Competitor,
} from '../types/project';

export const projectsAPI = {
  list: async (apiKey: string | null): Promise<Project[]> => {
    return apiCall<Project[]>(`${USER_SERVICE_URL}/api/projects`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  get: async (apiKey: string | null, id: string): Promise<Project> => {
    return apiCall<Project>(`${USER_SERVICE_URL}/api/projects/${id}`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  create: async (apiKey: string | null, data: NewProjectFormData): Promise<Project> => {
    return apiCall<Project>(`${USER_SERVICE_URL}/api/projects`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  update: async (apiKey: string | null, id: string, data: Partial<Project>): Promise<Project> => {
    return apiCall<Project>(`${USER_SERVICE_URL}/api/projects/${id}`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  delete: async (apiKey: string | null, id: string): Promise<void> => {
    await apiCall<void>(`${USER_SERVICE_URL}/api/projects/${id}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  addCompetitor: async (apiKey: string | null, projectId: string, url: string): Promise<Competitor> => {
    return apiCall<Competitor>(`${USER_SERVICE_URL}/api/projects/${projectId}/competitors`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify({ url }),
    });
  },

  removeCompetitor: async (apiKey: string | null, projectId: string, competitorId: string): Promise<void> => {
    await apiCall<void>(`${USER_SERVICE_URL}/api/projects/${projectId}/competitors/${competitorId}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },
};

// ============================================================================
// OAuth & Integration Types
// ============================================================================

export type OAuthGroup = 'google' | 'microsoft' | 'microsoft_ads' | 'meta' | 'linkedin' | 'tiktok';
export type IntegrationStatus = 'connected' | 'disconnected' | 'expired' | 'error';

export interface OAuthConnection {
  id: string;
  providerGroup: OAuthGroup;
  status: IntegrationStatus;
  accountEmail?: string;
  accountName?: string;
  scopes?: string[];
  connectedAt?: string;
  lastRefreshedAt?: string;
  errorMessage?: string;
}

export interface ApiKeyIntegration {
  id: string;
  provider: string;
  label?: string;
  status: IntegrationStatus;
  lastVerifiedAt?: string;
  errorMessage?: string;
}

// ============================================================================
// Integrations API (3-Level OAuth Architecture)
// ============================================================================

export interface OAuthConfigStatus {
  redirectUri: string;
  providers: { providerGroup: OAuthGroup; configured: boolean; source: string | null }[];
}

export interface PropertyItem {
  id: string;
  name: string;
  type?: string;
  url?: string;
}

export interface PropertyListResponse {
  properties: PropertyItem[];
  error?: string;
}

export const integrationsAPI = {
  // ---- OAuth Configuration Status ----

  getConfigStatus: async (apiKey: string | null): Promise<OAuthConfigStatus> => {
    return apiCall<OAuthConfigStatus>(`${USER_SERVICE_URL}/api/integrations/oauth/config-status`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  // ---- Level 2: OAuth Connections ----

  listConnections: async (apiKey: string | null): Promise<OAuthConnection[]> => {
    return apiCall<OAuthConnection[]>(`${USER_SERVICE_URL}/api/integrations/oauth/connections`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  initiateOAuth: async (apiKey: string | null, providerGroup: OAuthGroup): Promise<{ authorizationUrl: string; state: string }> => {
    return apiCall<{ authorizationUrl: string; state: string }>(`${USER_SERVICE_URL}/api/integrations/oauth/initiate`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify({ providerGroup, tenantOrigin: window.location.origin }),
    });
  },

  completeOAuth: async (apiKey: string | null, code: string, state: string): Promise<OAuthConnection> => {
    return apiCall<OAuthConnection>(`${USER_SERVICE_URL}/api/integrations/oauth/callback`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify({ code, state }),
    });
  },

  disconnectOAuth: async (apiKey: string | null, connectionId: string): Promise<void> => {
    await apiCall<void>(`${USER_SERVICE_URL}/api/integrations/oauth/connections/${connectionId}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  refreshOAuth: async (apiKey: string | null, connectionId: string): Promise<OAuthConnection> => {
    return apiCall<OAuthConnection>(`${USER_SERVICE_URL}/api/integrations/oauth/connections/${connectionId}/refresh`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  // ---- Level 3: Project Integrations ----

  listProjectIntegrations: async (apiKey: string | null, projectId: string): Promise<Integration[]> => {
    return apiCall<Integration[]>(`${USER_SERVICE_URL}/api/projects/${projectId}/integrations`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  assignToProject: async (
    apiKey: string | null,
    projectId: string,
    data: { oauthConnectionId: string; provider: IntegrationProvider; externalPropertyId: string; externalPropertyName?: string }
  ): Promise<Integration> => {
    return apiCall<Integration>(`${USER_SERVICE_URL}/api/projects/${projectId}/integrations`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  removeFromProject: async (apiKey: string | null, projectId: string, integrationId: string): Promise<void> => {
    await apiCall<void>(`${USER_SERVICE_URL}/api/projects/${projectId}/integrations/${integrationId}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  updateProjectIntegration: async (
    apiKey: string | null,
    projectId: string,
    integrationId: string,
    data: { config?: Record<string, unknown>; externalPropertyId?: string; externalPropertyName?: string }
  ): Promise<Integration> => {
    return apiCall<Integration>(`${USER_SERVICE_URL}/api/projects/${projectId}/integrations/${integrationId}`, {
      method: 'PATCH',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  // ---- API Key Integrations ----

  listApiKeys: async (apiKey: string | null): Promise<ApiKeyIntegration[]> => {
    return apiCall<ApiKeyIntegration[]>(`${USER_SERVICE_URL}/api/integrations/api-keys`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  addApiKey: async (
    apiKey: string | null,
    data: { provider: string; label?: string; apiKey: string | null; apiSecret?: string }
  ): Promise<ApiKeyIntegration> => {
    return apiCall<ApiKeyIntegration>(`${USER_SERVICE_URL}/api/integrations/api-keys`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  updateApiKey: async (
    apiKey: string | null,
    integrationId: string,
    data: { label?: string; apiKey?: string; apiSecret?: string }
  ): Promise<ApiKeyIntegration> => {
    return apiCall<ApiKeyIntegration>(`${USER_SERVICE_URL}/api/integrations/api-keys/${integrationId}`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  removeApiKey: async (apiKey: string | null, integrationId: string): Promise<void> => {
    await apiCall<void>(`${USER_SERVICE_URL}/api/integrations/api-keys/${integrationId}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  testApiKeyConnection: async (
    apiKey: string | null,
    integrationId: string
  ): Promise<{ success: boolean; message?: string; status?: string }> => {
    return apiCall<{ success: boolean; message?: string; status?: string }>(
      `${USER_SERVICE_URL}/api/integrations/api-keys/${integrationId}/test`,
      {
        method: 'POST',
        headers: { 'X-API-Key': apiKey ?? '' },
      }
    );
  },

  // ---- Property Discovery ----

  listGoogleSites: async (apiKey: string | null, connectionId: string): Promise<PropertyListResponse> => {
    return apiCall<PropertyListResponse>(
      `${USER_SERVICE_URL}/api/integrations/oauth/connections/${connectionId}/google/sites`,
      { headers: { 'X-API-Key': apiKey ?? '' } },
    );
  },

  listGoogleAnalyticsProperties: async (apiKey: string | null, connectionId: string): Promise<PropertyListResponse> => {
    return apiCall<PropertyListResponse>(
      `${USER_SERVICE_URL}/api/integrations/oauth/connections/${connectionId}/google/analytics-properties`,
      { headers: { 'X-API-Key': apiKey ?? '' } },
    );
  },

  listGoogleAdsAccounts: async (apiKey: string | null, connectionId: string): Promise<PropertyListResponse> => {
    return apiCall<PropertyListResponse>(
      `${USER_SERVICE_URL}/api/integrations/oauth/connections/${connectionId}/google/ads-accounts`,
      { headers: { 'X-API-Key': apiKey ?? '' } },
    );
  },

  listBingWebmasterSites: async (apiKey: string | null, connectionId: string): Promise<PropertyListResponse> => {
    return apiCall<PropertyListResponse>(
      `${USER_SERVICE_URL}/api/integrations/oauth/connections/${connectionId}/microsoft/webmaster-sites`,
      { headers: { 'X-API-Key': apiKey ?? '' } },
    );
  },

  listMicrosoftAdsAccounts: async (apiKey: string | null, connectionId: string): Promise<PropertyListResponse> => {
    return apiCall<PropertyListResponse>(
      `${USER_SERVICE_URL}/api/integrations/oauth/connections/${connectionId}/microsoft/ads-accounts`,
      { headers: { 'X-API-Key': apiKey ?? '' } },
    );
  },
};

// ============================================================================
// Feedback API
// ============================================================================

export const feedbackAPI = {
  submit: async (apiKey: string | null, data: { title: string; description: string; feedback_type: string; priority?: string; component?: string }): Promise<Record<string, unknown>> => {
    return apiCall<Record<string, unknown>>(`${USER_SERVICE_URL}/api/feedback`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },
};

// ============================================================================
// Connectors API (Global Definitions + Project Overrides)
// ============================================================================

export interface ConnectorDefinition {
  id: string;
  connectorType: string;
  displayName: string;
  description: string | null;
  category: string;
  authType: 'oauth' | 'api_key' | 'none';
  oauthGroup: string | null;
  icon: string | null;
  isEnabled: boolean;
  defaultConfig: Record<string, unknown>;
  requiredSettingsKeys: string[];
  sortOrder: number;
  createdAt: string | null;
  updatedAt: string | null;
  // Whether the underlying integration is actually connected (OAuth / API key)
  isAvailable: boolean;
  availabilityReason: string | null;
}

export interface ProjectConnector {
  id: string;
  projectId: string;
  connectorType: string;
  isEnabled: boolean;
  config: Record<string, unknown>;
  createdAt: string | null;
  updatedAt: string | null;
  // Merged from global definition
  displayName: string | null;
  description: string | null;
  category: string | null;
  authType: 'oauth' | 'api_key' | 'none' | null;
  oauthGroup: string | null;
  icon: string | null;
  globalEnabled: boolean | null;
  // Whether the underlying integration is actually connected (OAuth / API key)
  isAvailable: boolean;
  availabilityReason: string | null;
}

export const connectorsAPI = {
  /** List all global connector definitions */
  listDefinitions: async (apiKey: string | null): Promise<ConnectorDefinition[]> => {
    return apiCall<ConnectorDefinition[]>(`${USER_SERVICE_URL}/api/connectors`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  /** Update a global connector definition (admin only) */
  updateDefinition: async (
    apiKey: string | null,
    connectorType: string,
    data: { isEnabled?: boolean; displayName?: string; description?: string; defaultConfig?: Record<string, unknown>; sortOrder?: number },
  ): Promise<ConnectorDefinition> => {
    return apiCall<ConnectorDefinition>(`${USER_SERVICE_URL}/api/connectors/${connectorType}`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  /** List connectors for a project (global defs merged with project overrides) */
  listProjectConnectors: async (apiKey: string | null, projectId: string): Promise<ProjectConnector[]> => {
    return apiCall<ProjectConnector[]>(`${USER_SERVICE_URL}/api/projects/${projectId}/connectors`, {
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },

  /** Create or update a project-level connector override */
  upsertProjectConnector: async (
    apiKey: string | null,
    projectId: string,
    connectorType: string,
    data: { connectorType: string; isEnabled: boolean; config?: Record<string, unknown> },
  ): Promise<ProjectConnector> => {
    return apiCall<ProjectConnector>(`${USER_SERVICE_URL}/api/projects/${projectId}/connectors/${connectorType}`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey ?? '' },
      body: JSON.stringify(data),
    });
  },

  /** Remove a project-level connector override (reverts to global default) */
  deleteProjectConnector: async (apiKey: string | null, projectId: string, connectorType: string): Promise<void> => {
    await apiCall<void>(`${USER_SERVICE_URL}/api/projects/${projectId}/connectors/${connectorType}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey ?? '' },
    });
  },
};

export default {
  auth: authAPI,
  seo: seoAPI,
  campaign: campaignAPI,
  automation: automationAPI,
  settings: settingsAPI,
  health: healthAPI,
  projects: projectsAPI,
  integrations: integrationsAPI,
  feedback: feedbackAPI,
  connectors: connectorsAPI,
};
